
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useLocation, matchPath, useNavigate } from 'react-router-dom';
import { ThemeContext, DEFAULT_THEME, getShapeClasses } from '../context/ThemeContext.jsx';
import AlertNotificationCenter from './AlertNotificationCenter.jsx';
import UserProfileMenu from './UserProfileMenu.jsx';
import { DynamicIcon } from './icons.jsx';
import { LoadingSpinner } from './Common.jsx';
import { Avatar } from './Common.jsx';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api';
import { hasPermission, hasAnyModulePermission, isTenantScoped, isPlatformScoped } from '../utils/permissions';
import { getLogoLight, getLogoDark, getCollapsedLogo } from '../utils/branding';
import { PERMISSIONS } from '../constants/permissions';
import BRAND from '../config/branding';
import Watermark from './Watermark.jsx';
import GlobalSearchPalette from './GlobalSearchPalette.jsx';
import GlobalBreadcrumbs from './GlobalBreadcrumbs.jsx';
// import TenantLayout from './TenantLayout.jsx'; // REMOVED (Cleaned up)

// --- Page Imports ---
import AdminDashboardPage from '../pages/AdminDashboardPage.jsx';
import FlowsPage from '../pages/FlowsPage.tsx';
import WorklistStudioPage from '../pages/WorklistStudioPage.jsx';
import WorklistEditorPage from '../pages/WorklistEditorPage.jsx';
import WorklistRunnerPage from '../pages/WorklistRunnerPage.jsx';
import { STUDIOS } from '../constants/studios.js';


// These must match the content_types table. IDs never change; names can.
export const CONTENT_TYPE_IDS = {
   AI_ML: 1,  // AI/ML Predictions
   GENAI: 2,  // GenAI App
   DASHBOARD: 3,  // Dashboard
   WORKFLOW: 4,  // Workflow App
   DATASET: 5,  // Dataset Refresh
   ETL: 7,  // ETL
   OTHER: 99, // Other
};

// Helper: returns true if the given contentTypeId is an AI / ML / GenAI type
const isAiMlGenAi = (contentTypeId) =>
   contentTypeId === CONTENT_TYPE_IDS.AI_ML ||
   contentTypeId === CONTENT_TYPE_IDS.GENAI;

// Dashboard type (used for bundle content categorization)
const isDashboardType = (contentTypeId) =>
   contentTypeId === CONTENT_TYPE_IDS.DASHBOARD;

import DashboardsContainer from '../pages/dashboard/DashboardsContainer.tsx';
import DashboardsPage from '../pages/dashboard/DashboardsPage.tsx';
import DashboardsLandingPage from '../pages/dashboard/DashboardsLandingPage.tsx';
import DashboardViewPage from '../pages/dashboard/DashboardViewPage';
import PartnersPage from '../pages/PartnersPage.jsx';
import TenantsPage from '../pages/TenantsPage.jsx';
import UsersPage from '../pages/UsersPage.jsx';
import RolesPermissionsPage from '../pages/RolesPermissionsPage.jsx';
import BusinessAppsPage from '../pages/BusinessAppsPage.jsx';
import SettingsPage from '../pages/SettingsPage.jsx'; // Admin Settings
import LandingPage from '../pages/LandingPage.jsx';
import ExecutionLogViewer from './logs/ExecutionLogViewer.jsx';
import TenantUserGuidePage from '../pages/TenantUserGuidePage.jsx';

import TenantDetailPage from '../pages/TenantDetailPage.jsx';
import ContentsPage from '../pages/ContentsPage.jsx';
import PartnerDetailPage from '../pages/PartnerDetailPage.jsx';
import AssetDetailPage from '../pages/AssetDetailPage.jsx';
import PlansPage from '../pages/PlansPage.jsx';
import DataWorkspacePage from '../pages/DataWorkspacePage/DataWorkspacePage.jsx';
import DatasetsPage from "../pages/DatasetsPage";
import DataSourcesPage from "../pages/DataSourcesPage";
// import DataModelsPage from "../pages/DataModelsPage.tsx"; // NEW: Canvas-based designer
import DataModelsPage from "../pages/DataModelsPage";
import AutomationsPage from '../pages/AutomationsPage';
import AlertRulesPage from '../pages/AlertRulesPage';
import DemoManagementPage from '../pages/DemoManagementPage.jsx';
import AiInsightPage from '../pages/AiInsightPage.jsx';
import SemanticKnowledgeBasePage from '../pages/SemanticKnowledgeBasePage.jsx';
import PlatformSettings from '../pages/PlatformSettings.jsx';
import MLAppRunnerPage from '../pages/MLAppRunnerPage.jsx';
import CreditsPage from '../pages/CreditsPage.jsx';
import SupportPage from '../pages/SupportPage.jsx';
import AboutSettings from '../pages/AboutSettings.jsx';

import ProfilePage from '../pages/ProfilePage.jsx';
import DqProfilerPage from '../pages/DqProfilerPage.jsx';
import KeyRotationPage from '../pages/KeyRotationPage.jsx';
import AdminTrashPage from '../pages/AdminTrashPage.jsx';
import MyTrashPage from '../pages/MyTrashPage.jsx';
import AuditLogsPage from '../pages/AuditLogsPage.jsx';
import ComponentShowcasePage from '../pages/ComponentShowcasePage';


// --- Auth Context ---
import { useAuth } from '../context/useAuth';

//tenant layout
import TenantOnboardingPage from "../pages/TenantOnboardingPage.jsx";

// --- TENANT SPECIFIC IMPORTS ---
import TenantSettingsPage from '../pages/TenantSettingsPage.jsx';
import TenantUsersPage from '../pages/tenants/TenantUsersPage';
import MyAppsPage from '../pages/tenants/MyAppsPage';
import TenantAppRunnerPage from '../pages/TenantAppRunnerPage';
import TaskCenterPage from '../pages/tenants/TaskCenterPage.jsx';


// --- Page Permissions Map (Backend Sync) ---
import { TourEngine } from '../tours/TourEngine.jsx';
import { useTourController } from '../tours/useTourController.js';

const PAGE_PERMISSION_MAP = {
   dashboard: PERMISSIONS.DASHBOARD_MANAGE,
   tenants: PERMISSIONS.TENANTS_MANAGE,
   partners: PERMISSIONS.PARTNERS_MANAGE,
   users: PERMISSIONS.USERS_MANAGE,
   roles_permissions: PERMISSIONS.ROLES_READ,
   business_apps: PERMISSIONS.APPS_MANAGE,

   datasources: PERMISSIONS.DATASOURCES_MANAGE,
   datamodels: PERMISSIONS.DATA_MODELS_MANAGE,
   plans: PERMISSIONS.PLANS_MANAGE,
   platform_settings: PERMISSIONS.PLATFORM_SETTINGS_VIEW,
   settings: PERMISSIONS.SETTINGS_READ,
   dashboards: PERMISSIONS.DASHBOARD_MANAGE,
   flows: PERMISSIONS.FLOWS_MANAGE,
   automations: PERMISSIONS.SCHEDULER_MANAGE,
   alerts: PERMISSIONS.ALERTS_MANAGE,
   demo_management: 'page:demo_management:view',
   app_runner: PERMISSIONS.APPS_READ,
   home: null,
   logs: PERMISSIONS.LOGS_MANAGE,
   'ai-insight': PERMISSIONS.AI_READ,
   'semantic-kb': PERMISSIONS.KB_READ,
   support: PERMISSIONS.SUPPORT_MANAGE,
   'data-workspace': PERMISSIONS.APPS_MANAGE, // Mapping workspace to apps/content for now
   'dq-profiler': PERMISSIONS.DATA_PROFILE_READ,
   credits: PERMISSIONS.CREDITS_MANAGE,
   'my-trash': PERMISSIONS.LOGS_MANAGE, // Reuse logs or settings
   'admin-trash': PERMISSIONS.SETTINGS_MANAGE,
   'audit_logs': PERMISSIONS.AUDIT_LOGS_VIEW,
   'my-apps': PERMISSIONS.APPS_READ,
   'byor': PERMISSIONS.DASHBOARD_READ,
   'components': PERMISSIONS.PLATFORM_SETTINGS_MANAGE,
   'byom': PERMISSIONS.APPS_READ,
   tasks: PERMISSIONS.TASKS_VIEW,
   'worklist-studio': PERMISSIONS.WORKLISTS_MANAGE,
   'worklist-editor': PERMISSIONS.WORKLISTS_UPDATE,
   'worklist-runner': PERMISSIONS.WORKLISTS_READ,
};


// --- SIDEBAR RECONCILIATION STABILITY (FIX AUTO-DISMISS DROPDOWN BUG) ---
const SidebarContext = React.createContext(null);

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// SidebarTooltip â€” Portal-based tooltip for collapsed sidebar icons.
// Renders into document.body to escape overflow:hidden on the sidebar <aside>.
// Uses cloneElement to inject handlers/ref â€” adds ZERO wrapper DOM nodes.
// Appears to the RIGHT of the icon. 400ms enter delay, 100ms leave delay.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SidebarTooltip = React.memo(({ label, children, disabled = false }) => {
   const [visible, setVisible] = React.useState(false);
   const [pos, setPos] = React.useState({ top: 0, left: 0 });
   const anchorRef = React.useRef(null);
   const showTimer = React.useRef(null);
   const hideTimer = React.useRef(null);

   const handleMouseEnter = React.useCallback(() => {
      if (disabled) return;
      clearTimeout(hideTimer.current);
      // Capture position now so it's fresh when the tooltip appears
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) {
         setPos({ top: rect.top + rect.height / 2, left: rect.right + 10 });
      }
      showTimer.current = setTimeout(() => setVisible(true), 400);
   }, [disabled]);

   const handleMouseLeave = React.useCallback(() => {
      clearTimeout(showTimer.current);
      hideTimer.current = setTimeout(() => setVisible(false), 100);
   }, []);

   React.useEffect(() => () => {
      clearTimeout(showTimer.current);
      clearTimeout(hideTimer.current);
   }, []);

   // Inject ref + handlers into the single child element â€” no wrapper div needed
   const child = React.Children.only(children);
   const cloned = React.cloneElement(child, {
      ref: anchorRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
   });

   const tooltipEl = ReactDOM.createPortal(
      <div
         aria-hidden="true"
         style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: `translateY(-50%) translateX(${visible ? '0px' : '-6px'})`,
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.15s ease, transform 0.15s ease',
            zIndex: 99999,
            pointerEvents: 'none',
         }}
      >
         {/* Arrow pointing left toward the sidebar */}
         <div style={{
            position: 'absolute',
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderRight: '6px solid var(--sidebar-tooltip-bg, #1e293b)',
         }} />
         {/* Label pill */}
         <div style={{
            background: 'var(--sidebar-tooltip-bg, #1e293b)',
            color: 'var(--sidebar-tooltip-text, #f1f5f9)',
            borderRadius: '6px',
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 16px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.12)',
            letterSpacing: '0.01em',
         }}>
            {label}
         </div>
      </div>,
      document.body
   );

   return (
      <>
         {cloned}
         {tooltipEl}
      </>
   );
});
SidebarTooltip.displayName = 'SidebarTooltip';

const RenderMenuItem = React.memo(({ item, isMobile = false }) => {
   const context = React.useContext(SidebarContext);
   const location = useLocation();
   const navigate = useNavigate();

   if (!context) return null;

   const {
      activePage,
      tenantAppRunnerProps,
      dashboardEditorState,
      isSidebarOpen,
      sidebarExpandedRef,
      setSidebarExpandTick,
      setActivePage,
      setMobileSidebarOpen,
      handleBack,
      handleNavigate,
      isTenantUser
   } = context;

   // Active Logic:
   // 1. Exact Match (Page ID)
   // 2. Tenant App Runner (App/Bundle ID)
   // 3. Flow/Project Editor (Flow ID)

   let isActive = activePage === item.id || (item.id === 'home' && (activePage === 'dashboard' || activePage === 'landing'));

   if (activePage === 'tenant_app_runner') {
      const uid = tenantAppRunnerProps?.appUid;
      const bid = tenantAppRunnerProps?.bundleId;
      isActive =
         item.id === `app_${uid}` ||
         item.id === `bundle_${bid}` ||
         item.id === `proj_${uid}`;
   } else if (activePage === 'myapps_dashboard') {
      const did = dashboardEditorState?.dashboardId;
      isActive = item.id === `dash_${did}`;
   }

   const hasChildren = item.children && item.children.length > 0;

   const hasActiveChild = hasChildren && item.children.some(child => {
      if (child.type === 'group') {
         return child.children?.some(sub => {
            if (activePage === 'tenant_app_runner') {
               const uid = tenantAppRunnerProps?.appUid;
               const bid = tenantAppRunnerProps?.bundleId;
               return sub.id === `app_${uid}` || sub.id === `bundle_${bid}` || sub.id === `proj_${uid}`;
            }
            if (activePage === 'myapps_dashboard') {
               return sub.id === `dash_${dashboardEditorState?.dashboardId}`;
            }
            return activePage === sub.id;
         });
      }
      if (activePage === 'tenant_app_runner') {
         const uid = tenantAppRunnerProps?.appUid;
         const bid = tenantAppRunnerProps?.bundleId;
         return child.id === `app_${uid}` || child.id === `bundle_${bid}` || child.id === `proj_${uid}`;
      }
      if (activePage === 'myapps_dashboard') {
         return child.id === `dash_${dashboardEditorState?.dashboardId}`;
      }
      return activePage === child.id;
   });

   const localExpanded = (item.id in sidebarExpandedRef.current)
      ? sidebarExpandedRef.current[item.id]
      : hasActiveChild;

   const setLocalExpanded = (valOrFn) => {
      const prev = sidebarExpandedRef.current[item.id] ?? hasActiveChild;
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      sidebarExpandedRef.current[item.id] = next;
      setSidebarExpandTick(t => t + 1);
   };

   React.useEffect(() => {
      if (hasActiveChild && !sidebarExpandedRef.current[item.id]) {
         sidebarExpandedRef.current[item.id] = true;
         setSidebarExpandTick(t => t + 1);
      }
   }, [hasActiveChild, item.id, sidebarExpandedRef, setSidebarExpandTick]);
   const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
   const isMenuOpen = Boolean(menuAnchorEl);
   const handleMenuClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
   };
   const handleMenuClose = () => {
      setMenuAnchorEl(null);
   };

   if (hasChildren && !isSidebarOpen) {
      return (
         <div className="mb-1" id={isMobile ? `mobile-sidebar-${item.id.replace(/_/g, '-')}` : `sidebar-${item.id.replace(/_/g, '-')}`}>
            <SidebarTooltip label={item.label}>
               <button
                  id={isMobile ? `mobile-sidebar-btn-${item.id.replace(/_/g, '-')}` : `sidebar-btn-${item.id.replace(/_/g, '-')}`}
                  onClick={handleMenuClick}
                  className={`w-full flex items-center py-3 rounded-lg transition-colors duration-200 group justify-center ${hasActiveChild
                     ? 'bg-primary text-[var(--primary-foreground)] shadow-md'
                     : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5'
                     }`}
               >
                  <DynamicIcon name={item.icon || 'Circle'} className={`w-5 h-5 min-w-[20px] overflow-visible transition-transform group-hover:scale-110 ${hasActiveChild ? 'text-[var(--primary-foreground)]' : 'text-gray-500 dark:text-gray-400 group-hover:text-primary dark:group-hover:text-primary'}`} />
               </button>
            </SidebarTooltip>
            <Menu
               anchorEl={menuAnchorEl}
               open={isMenuOpen}
               onClose={handleMenuClose}
               anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
               transformOrigin={{ vertical: 'top', horizontal: 'left' }}
               slotProps={{
                  paper: {
                     style: {
                        maxHeight: 400,
                        width: 250,
                     }
                  }
               }}
            >
               <div className="px-4 py-1.5 text-xs font-bold text-gray-550 border-b border-gray-100 dark:border-gray-800 mb-1 select-none">
                  {item.label}
               </div>
               {item.children.map(child => {
                  if (child.type === 'group') {
                     return (
                        <div key={child.id} className="py-1">
                           <div className="px-4 py-1 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4 select-none">
                              {child.label}
                           </div>
                           {child.children?.map(sub => {
                              let subActive = false;
                              if (activePage === 'tenant_app_runner') {
                                 const uid = tenantAppRunnerProps?.appUid;
                                 const bid = tenantAppRunnerProps?.bundleId;
                                 const cid = tenantAppRunnerProps?.contentUid;
                                 subActive = sub.id === `app_${uid}` || sub.id === `bundle_${bid}` || sub.id === `proj_${uid}` || (cid && sub.id.endsWith(`_${cid}`));
                              } else if (activePage === 'myapps_dashboard') {
                                 subActive = sub.id === `dash_${dashboardEditorState?.dashboardId}`;
                              } else {
                                 subActive = activePage === sub.id;
                              }
                              return (
                                 <MenuItem
                                    key={sub.id}
                                    onClick={() => {
                                       handleMenuClose();
                                       if (sub.action) sub.action();
                                       else { handleBack(); setActivePage(sub.id); }
                                    }}
                                    selected={subActive}
                                 >
                                    <ListItemIcon>
                                       <DynamicIcon name={sub.icon || 'Circle'} className={`w-4 h-4 ${subActive ? 'text-primary' : 'text-gray-500'}`} />
                                    </ListItemIcon>
                                    <ListItemText
                                       primary={sub.label}
                                       primaryTypographyProps={{ style: { fontSize: '0.875rem' } }}
                                    />
                                 </MenuItem>
                              );
                           })}
                        </div>
                     );
                  }
                  let childActive = activePage === child.id;
                  if (activePage === 'tenant_app_runner') {
                     const uid = tenantAppRunnerProps?.appUid;
                     const bid = tenantAppRunnerProps?.bundleId;
                     childActive = child.id === `app_${uid}` || child.id === `bundle_${bid}` || child.id === `proj_${uid}`;
                  } else if (activePage === 'myapps_dashboard') {
                     childActive = child.id === `dash_${dashboardEditorState?.dashboardId}`;
                  }
                  return (
                     <MenuItem
                        key={child.id}
                        onClick={() => {
                           handleMenuClose();
                           if (child.action) child.action();
                           else { handleNavigate(child.id); }
                        }}
                        selected={childActive}
                     >
                        <ListItemIcon>
                           <DynamicIcon name={child.icon || 'Circle'} className={`w-4 h-4 ${childActive ? 'text-primary' : 'text-gray-500'}`} />
                        </ListItemIcon>
                        <ListItemText
                           primary={child.label}
                           primaryTypographyProps={{ style: { fontSize: '0.875rem' } }}
                        />
                     </MenuItem>
                  );
               })}
            </Menu>
         </div>
      );
   }

   if (hasChildren && isSidebarOpen) {
      return (
         <div className="mb-1" id={isMobile ? `mobile-sidebar-${item.id.replace(/_/g, '-')}` : `sidebar-${item.id.replace(/_/g, '-')}`}>
            <div className={`w-full flex items-center px-4 py-2.5 rounded-lg transition-colors group ${hasActiveChild ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
               <DynamicIcon name={item.icon} className={`w-5 h-5 mr-3 shrink-0 ${hasActiveChild ? 'text-blue-500' : 'text-gray-500'}`} />
               <button
                  className="font-medium whitespace-nowrap flex-1 text-left text-sm hover:text-primary transition-colors"
                  onClick={() => {
                     if (item.sectionAction) item.sectionAction();
                     else if (item.action) {
                        if (!localExpanded) {
                           item.action();
                           setLocalExpanded(true);
                        } else {
                           setLocalExpanded(false);
                        }
                     } else setLocalExpanded(v => !v);
                  }}
               >
                  {item.label}
               </button>
               <button onClick={() => setLocalExpanded(v => !v)} className="p-1 -mr-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                  <DynamicIcon name="ChevronDown" className={`w-4 h-4 transition-transform ${localExpanded ? 'rotate-180' : ''}`} />
               </button>
            </div>
            <div className={`overflow-hidden transition-all duration-300 ${localExpanded ? 'max-h-[2000px] opacity-100 pb-1' : 'max-h-0 opacity-0'}`}>
               {item.children.map(child => {
                  if (child.type === 'group') {
                     return (
                        <div key={child.id} className="mt-1">
                           <div className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-12">{child.label}</div>
                           {child.children.map(sub => {
                              let subActive = false;
                              if (activePage === 'tenant_app_runner') {
                                 const uid = tenantAppRunnerProps?.appUid;
                                 const bid = tenantAppRunnerProps?.bundleId;
                                 const cid = tenantAppRunnerProps?.contentUid;
                                 subActive = sub.id === `app_${uid}` || sub.id === `bundle_${bid}` || sub.id === `proj_${uid}` || (cid && sub.id.endsWith(`_${cid}`));
                              } else if (activePage === 'myapps_dashboard') {
                                 subActive = sub.id === `dash_${dashboardEditorState?.dashboardId}`;
                              }
                              return (
                                 <button
                                    id={isMobile ? `mobile-sidebar-${sub.id.replace(/_/g, '-')}` : `sidebar-${sub.id.replace(/_/g, '-')}`}
                                    data-tour={`tour-${sub.id.replace(/_/g, '-')}`}
                                    key={sub.id}
                                    onClick={() => {
                                       if (sub.action) sub.action();
                                       else { handleBack(); setActivePage(sub.id); }
                                       setMobileSidebarOpen(false);
                                    }}
                                    className={`w-full flex items-start text-left px-4 py-2 pl-12 rounded-lg transition-colors ${subActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-black/5 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300'}`}
                                 >
                                    <DynamicIcon name={sub.icon} className={`w-4 h-4 mr-2 mt-0.5 shrink-0 ${subActive ? 'text-primary' : 'opacity-70'}`} />
                                    <span className="text-sm truncate text-left min-w-0 flex-1">{sub.label}</span>
                                    <span className="ml-auto flex items-center gap-1 shrink-0">
                                       {sub.sharedBy && (
                                          <span
                                             title={`Shared by ${sub.sharedBy}`}
                                             className="w-2 h-2 rounded-full bg-blue-400 dark:bg-blue-500 ring-1 ring-white dark:ring-slate-900 mt-0.5"
                                          />
                                       )}
                                       {subActive && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1" />}
                                    </span>
                                 </button>
                              );
                           })}
                        </div>
                     )
                  }
                  let childActive = activePage === child.id;
                  if (activePage === 'tenant_app_runner') {
                     const uid = tenantAppRunnerProps?.appUid;
                     const bid = tenantAppRunnerProps?.bundleId;
                     childActive = child.id === `app_${uid}` || child.id === `bundle_${bid}` || child.id === `proj_${uid}`;
                  } else if (activePage === 'myapps_dashboard') {
                     childActive = child.id === `dash_${dashboardEditorState?.dashboardId}`;
                  }

                  return (
                     <button
                        id={isMobile ? `mobile-sidebar-${child.id.replace(/_/g, '-')}` : `sidebar-${child.id.replace(/_/g, '-')}`}
                        key={child.id}
                        onClick={() => {
                           if (child.action) child.action();
                           else { handleNavigate(child.id); }
                           setMobileSidebarOpen(false);
                        }}
                        className={`w-full flex items-start text-left px-4 py-2 pl-12 rounded-lg transition-colors ${childActive ? 'bg-primary/10 dark:bg-primary/20 text-primary font-medium' : 'hover:bg-black/5 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300'}`}
                     >
                        {child.icon && <DynamicIcon name={child.icon} className={`w-4 h-4 mr-2 ${childActive ? 'text-primary' : 'opacity-70'}`} />}
                        <span className="text-sm truncate text-left min-w-0 flex-1">{child.label}</span>
                        <span className="ml-auto flex items-center gap-1 shrink-0">
                           {child.sharedBy && (
                              <span
                                 title={`Shared by ${child.sharedBy}`}
                                 className="w-2 h-2 rounded-full bg-blue-400 dark:bg-blue-500 ring-1 ring-white dark:ring-slate-900"
                              />
                           )}
                           {childActive && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        </span>
                     </button>
                  )
               })}
            </div>
         </div>
      );
   }

   return (
      <SidebarTooltip label={item.label} disabled={isSidebarOpen}>
         <button
            id={isMobile ? (item.id === 'landing' || item.id === 'dashboard' ? 'mobile-sidebar-home' : `mobile-sidebar-${item.id.replace(/_/g, '-')}`) : (item.id === 'landing' || item.id === 'dashboard' ? 'sidebar-home' : `sidebar-${item.id.replace(/_/g, '-')}`)}
            onClick={() => {
               handleBack();
               if (item.action) item.action();
               else if (item.id === "home") {
                  setActivePage(isTenantUser ? 'landing' : 'dashboard');
                  navigate("/");
               }
               else setActivePage(item.id);
            }}
            className={`w-full flex items-center py-3 rounded-lg group ${isActive
               ? 'bg-primary text-[var(--primary-foreground)] shadow-md'
               : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5'
               } ${isSidebarOpen ? '' : 'justify-center'}`}
            style={{
               paddingLeft: isSidebarOpen ? '16px' : '0px',
               paddingRight: isSidebarOpen ? '16px' : '0px',
               transition: 'padding 0.32s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.2s, color 0.2s',
            }}
         >
            <DynamicIcon name={item.icon || 'Circle'} className={`w-5 h-5 min-w-[20px] overflow-visible transition-transform group-hover:scale-110 ${isActive ? 'text-[var(--primary-foreground)]' : 'text-gray-500 dark:text-gray-400 group-hover:text-primary dark:group-hover:text-primary'}`} />
            <span
               className="font-medium truncate"
               style={{
                  transition: 'opacity 0.22s cubic-bezier(0.25, 0.8, 0.25, 1), max-width 0.28s cubic-bezier(0.25, 0.8, 0.25, 1), margin-left 0.28s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  maxWidth: isSidebarOpen ? '200px' : '0px',
                  opacity: isSidebarOpen ? 1 : 0,
                  marginLeft: isSidebarOpen ? '12px' : '0px',
                  overflow: 'hidden',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  whiteSpace: 'nowrap'
               }}
            >
               {item.label}
            </span>
         </button>
      </SidebarTooltip>
   );
});
RenderMenuItem.displayName = 'RenderMenuItem';

const PlatformLayout = ({ token }) => {

   const { user: currentUser, loading: userLoading, refreshUser: refresh, logout, pwdExp, branding } = useAuth();
   const userError = null; // AuthContext handles errors internally via token state
   const themeCtx = useContext(ThemeContext) || {};
   const { theme = DEFAULT_THEME, setTheme = () => { } } = themeCtx;



   const location = useLocation();
   const navigate = useNavigate();

   const SIDEBAR_MIN = 180;
   const SIDEBAR_MAX = 420;
   const SIDEBAR_DEFAULT = 240;
   const SIDEBAR_COLLAPSED = 64;

   // Hover-expand sidebar state
   const [sidebarLocked, setSidebarLocked] = useState(() => {
      const saved = localStorage.getItem('sidebar_locked');
      return saved === 'true'; // defaults false (collapsed)
   });
   const [sidebarHovered, setSidebarHovered] = useState(false);
   // Effective expanded = locked OR hovered
   const isExpanded = sidebarLocked || sidebarHovered;
   // Derived from isExpanded — legacy call sites that call setSidebarOpen still work as no-ops
   const isSidebarOpen = isExpanded;
   const setSidebarOpen = () => { }; // shim — sidebar open/close now driven by hover/lock
   const hoverLeaveTimer = React.useRef(null);
   const [sidebarWidth, setSidebarWidth] = useState(() => {
      const saved = localStorage.getItem('platform_sidebar_width');
      return saved ? parseInt(saved, 10) : SIDEBAR_DEFAULT;
   });
   const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
   const currentW = isExpanded ? sidebarWidth : SIDEBAR_COLLAPSED;
   const contentOffsetW = sidebarLocked ? sidebarWidth : SIDEBAR_COLLAPSED;


   // Persist sidebar width
   useEffect(() => {
      if (isSidebarOpen) localStorage.setItem('platform_sidebar_width', String(sidebarWidth));
   }, [sidebarWidth, isSidebarOpen]);

   // Drag-to-resize handler
   const isResizingRef = useRef(false);
   const [isResizing, setIsResizing] = useState(false);
   const handleResizeStart = useCallback((e) => {
      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      const startX = e.clientX;
      const startW = sidebarWidth;
      const onMove = (ev) => {
         if (!isResizingRef.current) return;
         const delta = ev.clientX - startX;
         const next = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startW + delta));
         setSidebarWidth(next);
      };
      const onUp = () => {
         isResizingRef.current = false;
         setIsResizing(false);
         window.removeEventListener('mousemove', onMove);
         window.removeEventListener('mouseup', onUp);
         document.body.style.cursor = '';
         document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
   }, [sidebarWidth]);
   const [activePage, setActivePage] = useState(() => {
      const path = window.location.pathname;
      if (path === '/home' || path === '/' || path === '') return 'home';
      // Robust segment extraction (ignore leading slash)
      const segment = path.split('/').filter(Boolean)[0];
      return segment || 'dashboard';
   });


   const [profileTab, setProfileTab] = useState(null);

   const [editingContentId, setEditingContentId] = useState(null);
   const [viewingTenantId, setViewingTenantId] = useState(null);
   const [viewingPartnerId, setViewingPartnerId] = useState(null);
   const [viewingAsset, setViewingAsset] = useState(null);

   // Dashboard Editor State
   const [dashboardEditorState, setDashboardEditorState] = useState(null); // { mode: 'view' | 'edit' | 'new', dashboardId?: string }
   const [previewContentId, setPreviewContentId] = useState(null);

   //tenantonboarding
   const [onboardingTenantId, setOnboardingTenantId] = useState(null);

   // --- Password Expiry Banner Logic (For imminent expiry: Day 0-1) ---




   // --- TENANT SPECIFIC STATE ---
   // isTenantScoped uses roleScope JWT claim works for built-in (r4/r7) AND custom tenant roles
   const isTenantUser = isTenantScoped(currentUser);
   const [showTenantGuide, setShowTenantGuide] = useState(false);
   const [tenantFeatures, setTenantFeatures] = useState({});

   // Unified sidebar data (features + content) â€” single API call
   const [myContent, setMyContent] = useState(null);
   const [contentLoading, setContentLoading] = useState(false);
   const refreshContent = React.useCallback(() => {
      if (!currentUser?.tenantId || !token) return;
      setContentLoading(true);
      apiFetch(`/tenants/${currentUser.tenantId}/sidebar-data`, token)
         .then(data => {
            if (data) {
               const { features, ...content } = data;
               setTenantFeatures(features || {});
               setMyContent(content);
            }
         })
         .catch(e => console.error('[PlatformLayout] sidebar-data refresh failed', e))
         .finally(() => setContentLoading(false));
   }, [currentUser?.tenantId, token]);

   // Real-time Sidebar Sync Listener
   useEffect(() => {
      const handleRefresh = () => {
         console.log('[PlatformLayout] Real-time content refresh triggered');
         refreshContent();
      };
      window.addEventListener('refresh-my-content', handleRefresh);
      return () => window.removeEventListener('refresh-my-content', handleRefresh);
   }, [refreshContent]);

   useEffect(() => {
      window.addEventListener('refresh-sidebar', refreshContent);
      return () => window.removeEventListener('refresh-sidebar', refreshContent);
   }, [refreshContent]);


   // Tenant App Runner State
   const [tenantAppRunnerProps, setTenantAppRunnerProps] = useState(null);

   // --- SIDEBAR ACCORDION STATES (must live here, not inside inline Sidebar, to survive re-renders) ---
   const [isAidasAppsExpanded, setAidasAppsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_aidas_apps_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isPublishedAppsExpanded, setPublishedAppsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_published_apps_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isMyAppsExpanded, setMyAppsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_myapps_expanded');
      if (saved !== null) return JSON.parse(saved);
      const path = window.location.pathname;
      return path.startsWith('/tenant/app') || path.startsWith('/flows') || path.startsWith('/analytics/byor');
   });
   const [isSettingsExpanded, setSettingsExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_settings_expanded');
      return saved !== null ? JSON.parse(saved) : false;
   });
   const [isAdminExpanded, setAdminExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_admin_expanded');
      return saved !== null ? JSON.parse(saved) : false;
   });
   const [isCreatorExpanded, setCreatorExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_creator_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isAppStudioExpanded, setAppStudioExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_app_studio_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [isDataExpanded, setDataExpanded] = useState(() => {
      const saved = localStorage.getItem('platform_data_expanded');
      return saved !== null ? JSON.parse(saved) : true;
   });

   // Stable ref-map for accordion group expand state inside the inline Sidebar.
   // useRef survives parent re-renders; sidebarExpandTick forces a re-render when map changes.
   const sidebarExpandedRef = React.useRef({});
   const [sidebarExpandTick, setSidebarExpandTick] = useState(0);
   const [sidebarActiveView, setSidebarActiveView] = useState('main'); // 'main', 'aidas_apps', 'published_apps', 'my_apps', 'administration', 'settings'
   // Persists nav scroll position across Sidebar remounts (Sidebar is inline so DOM resets on every parent render)
   const navScrollRef = React.useRef(0);

   // Reset sidebar scroll position to top whenever active view changes
   useEffect(() => {
      navScrollRef.current = 0;
      // Panel 1 scroll container can be reset by setting navScrollRef to 0.
      // Panel 2 has its own inner overflow-y-auto scroll container which we target here.
      const panel2ScrollContainer = document.querySelector('#sidebar-nav-container div:nth-child(2) > div:last-child');
      if (panel2ScrollContainer) {
         panel2ScrollContainer.scrollTop = 0;
      }
   }, [sidebarActiveView]);

   // ... (Rest of state)

   // Sync accordion states to localStorage (so expand preference survives page refresh)
   useEffect(() => {
      localStorage.setItem('platform_aidas_apps_expanded', JSON.stringify(isAidasAppsExpanded));
      localStorage.setItem('platform_published_apps_expanded', JSON.stringify(isPublishedAppsExpanded));
      localStorage.setItem('platform_myapps_expanded', JSON.stringify(isMyAppsExpanded));
      localStorage.setItem('platform_settings_expanded', JSON.stringify(isSettingsExpanded));
      localStorage.setItem('platform_admin_expanded', JSON.stringify(isAdminExpanded));
      localStorage.setItem('platform_creator_expanded', JSON.stringify(isCreatorExpanded));
      localStorage.setItem('platform_app_studio_expanded', JSON.stringify(isAppStudioExpanded));
      localStorage.setItem('platform_data_expanded', JSON.stringify(isDataExpanded));
   }, [
      isAidasAppsExpanded,
      isPublishedAppsExpanded,
      isMyAppsExpanded,
      isSettingsExpanded,
      isAdminExpanded,
      isCreatorExpanded,
      isAppStudioExpanded,
      isDataExpanded
   ]);


   const hasAccess = (pageId) => {
      // â”€â”€ Worklist pages: two-layer gate â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      // Layer 1 (tenant users): feature flag must be enabled by the tenant.
      // Layer 2 (all users):    granular RBAC permission per page.
      //   worklist-studio â†’ WORKLISTS_MANAGE (create/configure worklists)
      //   worklist-editor â†’ WORKLISTS_UPDATE (edit existing worklists)
      //   worklist-runner â†’ WORKLISTS_READ   (read-only task runner)
      if (pageId === 'worklist-studio') {
         if (isTenantUser && !tenantFeatures?.worklist_studio) return false;
         return hasPermission(currentUser, PERMISSIONS.WORKLISTS_MANAGE);
      }
      if (pageId === 'worklist-editor') {
         if (isTenantUser && !tenantFeatures?.worklist_studio) return false;
         return hasPermission(currentUser, PERMISSIONS.WORKLISTS_UPDATE);
      }
      if (pageId === 'worklist-runner') {
         if (isTenantUser && !tenantFeatures?.worklist_studio) return false;
         return hasPermission(currentUser, PERMISSIONS.WORKLISTS_READ);
      }
      if (pageId === 'data-workspace') {
         if (currentUser?.roleScope === 'Partner' || currentUser?.roleId === 'r3') return false;
         if (!hasPermission(currentUser, PERMISSIONS.APPS_MANAGE)) return false;
      }
      if (pageId === 'dq-profiler' && !hasPermission(currentUser, PERMISSIONS.DATASOURCES_READ) && (currentUser?.roleScope === 'Partner' || currentUser?.roleId === 'r3')) {
         return false;
      }

      // Platform-only pages -- never allow tenant users under any circumstances
      if (isTenantUser) {
         if (['roles_permissions', 'partners', 'tenants', 'plans', 'demo_management', 'platform_settings'].includes(pageId)) return false;
      }

      // Mapping of page IDs to module names for "Any Permission" check
      // Mapping of page IDs to module names for "Any Permission" check
      // If a user has ANY permission for a module (e.g. apps:create), they should see the module.
      const moduleMap = {
         tenants: 'tenants',
         partners: 'partners',
         users: 'users',
         roles_permissions: 'roles',
         business_apps: 'apps',
         dashboard: 'dashboard',
         dashboards: 'dashboard',
         flows: 'flows',
         automations: 'scheduler',
         alerts: 'alerts',
         'semantic-kb': 'knowledge_base',
         support: 'support',
         credits: 'credits',
         plans: 'plans',
         platform_settings: 'settings',
         settings: 'settings',
         datasources: 'datasources',
         'admin-trash': 'settings',
         'my-trash': 'activity_logs',
         contents: 'content',
         logs: 'activity_logs',
         'ai-insight': 'ai_insight',
         demo_management: 'public_demo',
         datamodels: 'data_models',
         tasks: 'tasks',
         audit_logs: 'audit_logs',
         'worklist-studio': 'worklists',
         'worklist-editor': 'worklists',
         'worklist-runner': 'worklists',
         'profile': 'users',
         'components': 'settings'
      };

      const moduleName = moduleMap[pageId];
      if (moduleName) {
         return hasAnyModulePermission(currentUser, moduleName);
      }

      // r1 bypass is handled inside hasPermission()
      if (isTenantUser) {
         // Platform-only pages -- never allow tenant users
         if (['roles_permissions', 'partners', 'tenants', 'plans', 'demo_management', 'platform_settings'].includes(pageId)) return false;

         // Always-open basic pages (no RBAC check for landing/home)
         if (['landing', 'home'].includes(pageId)) return true;

         // My Apps -- always open for all tenants
         if (pageId === 'my-apps') return true;

         // Studio pages: gated by tenantFeatures AND RBAC (tenant-level feature toggle + user-level permission)
         if (pageId === 'flows') {
            return (
               (!!tenantFeatures?.workflow_studio && hasAnyModulePermission(currentUser, 'flows')) ||
               (!!tenantFeatures?.ml_studio && hasAnyModulePermission(currentUser, 'content')) ||
               (!!tenantFeatures?.data_studio && hasAnyModulePermission(currentUser, 'data_models'))
            );
         }
         if (pageId === STUDIOS.WORKFLOW) return !!tenantFeatures?.workflow_studio && hasAnyModulePermission(currentUser, 'flows');
         if (pageId === 'contents' || pageId === STUDIOS.ML) return !!tenantFeatures?.ml_studio && hasAnyModulePermission(currentUser, 'content');
         if (pageId === STUDIOS.DATA) return !!tenantFeatures?.data_studio && hasAnyModulePermission(currentUser, 'data_models');
         if (pageId === 'dashboards') return !!tenantFeatures?.dashboard_studio && hasAnyModulePermission(currentUser, 'dashboard');
         if (pageId === 'business_apps') return !!tenantFeatures?.app_marketplace && hasAnyModulePermission(currentUser, 'apps');


         // Feature + RBAC: must be both feature-enabled AND permission-granted
         if (pageId === 'ai-insight')
            return !!(tenantFeatures?.ai_insight || tenantFeatures?.AIINSIGHT_NLQ) && hasPermission(currentUser, PAGE_PERMISSION_MAP['ai-insight'] ?? PERMISSIONS.AI_READ);
         if (pageId === 'semantic-kb')
            return tenantFeatures?.semantic_kb !== false && hasPermission(currentUser, PERMISSIONS.KB_READ);
         if (pageId === 'data-workspace')
            return !!(tenantFeatures?.unified_data_editor) && hasPermission(currentUser, PERMISSIONS.APPS_MANAGE);

         // All other pages (dq-profiler + future pages): check PAGE_PERMISSION_MAP
         const requiredPermission = PAGE_PERMISSION_MAP[pageId];
         if (requiredPermission) return hasPermission(currentUser, requiredPermission);

         return true; // No permission defined = open page
      }

      // Granular Permission Check for Platform/Partner roles
      const requiredPermission = PAGE_PERMISSION_MAP[pageId];
      if (!requiredPermission) return true; // No permission required -- allow
      return hasPermission(currentUser, requiredPermission);
   };
   // Global search items dynamically filtered by permissions
   const getSearchItems = () => {
      const rawItems = [
         { id: 'home', label: 'Home', icon: 'Home', category: 'Navigation' },
         { id: 'logs', label: 'Execution Status', icon: 'FileText', category: 'Navigation' },
         { id: 'flows', label: 'Flows', icon: 'GitBranch', category: 'Creator Studio' },
         { id: 'ai-insight', label: 'Agentic Insights', icon: 'Sparkles', category: 'Creator Studio' },
         { id: 'dashboards', label: 'Dashboards', icon: 'LayoutDashboard', category: 'Creator Studio' },
         { id: 'business_apps', label: 'Business Apps', icon: 'Package', category: 'Creator Studio' },
         { id: 'data-workspace', label: 'Unified Data Workspace', icon: 'Database', category: 'App Studio' },
         { id: 'dq-profiler', label: 'Data Profiler', icon: 'Activity', category: 'App Studio' },
         { id: 'automations', label: 'Scheduler', icon: 'Calendar', category: 'Creator Studio' },
         { id: 'alerts', label: 'Alert Rules', icon: 'Bell', category: 'Creator Studio' },
         { id: 'partners', label: 'Partners', icon: 'Briefcase', category: 'Administration' },
         { id: 'tenants', label: 'Tenants', icon: 'Globe', category: 'Administration' },
         { id: 'users', label: 'Users', icon: 'Users', category: 'Administration' },
         { id: 'roles_permissions', label: 'Roles & Permissions', icon: 'Shield', category: 'Administration' },
         { id: 'plans', label: 'Plans & Tiers', icon: 'CreditCard', category: 'Administration' },
         { id: 'credits', label: 'Credit Management', icon: 'Coins', category: 'Administration' },
         { id: 'support', label: 'Support Tickets', icon: 'LifeBuoy', category: 'Administration' },
         { id: 'demo_management', label: 'Public Demos', icon: 'Tv', category: 'Administration' },
         { id: 'platform_settings', label: 'Settings', icon: 'Settings', category: 'Administration' },
         { id: 'settings', label: 'Tenant Settings', icon: 'Settings', category: 'Settings' },
         { id: 'my-trash', label: 'Recycle Bin', icon: 'Trash2', category: 'Settings' },
         { id: 'semantic-kb', label: 'Knowledge base', icon: 'Brain', category: 'Data Settings' },
         { id: 'datasources', label: 'Data Sources', icon: 'CableIcon', category: 'Data Settings' },
         { id: 'datamodels', label: 'Data Models', icon: 'Database', category: 'Data Settings' },
         { id: 'worklist-studio', label: 'Worklist Studio', icon: 'ClipboardList', category: 'App Studio' },
         { id: 'worklist-editor', label: 'Worklist Editor', icon: 'FilePen', category: 'App Studio' },
         { id: 'worklist-runner', label: 'Worklist Runner', icon: 'Play', category: 'App Studio' },
         { id: 'tasks', label: 'Task Center', icon: 'ListChecks', category: 'Navigation' },
         { id: 'audit_logs', label: 'Audit Logs', icon: 'ClipboardList', category: 'Administration' },
         { id: 'admin-trash', label: 'Trash Bin', icon: 'Trash2', category: 'Settings' },
         { id: 'profile', label: 'My Profile', icon: 'User', category: 'Settings' }
      ];
      return rawItems.filter(item => hasAccess(item.id)).map(item => ({
         id: item.id,
         type: 'action',
         title: item.label,
         description: item.category,
         icon: item.icon
      }));
   };

   const quickActions = getSearchItems();

   const handleGlobalSearchNavigate = (item) => {
      const type = item.type;
      const id = item.id;

      if (type === 'action') {
         navigate(`/${id}`);
      } else if (type === 'dashboard') {
         navigate(`/dashboards/${id}/view`);
      } else if (type === 'flow') {
         navigate(`/flows/${id}`);
      } else if (type === 'app') {
         navigate(appPath(currentUser?.tenantId, id));
      } else {
         // Fallback for actual backend entities like user, tenant
         const pageId = type + 's';
         navigate(`/${pageId}`);
      }
   };

   // Builds the canonical tenant-scoped URL for a business app (Option A).
   // Falls back to legacy /tenant/app/... if tenantId is not yet available.
   const appPath = (tenantId, bundleId, contentUid = null) => {
      const base = tenantId ? `/${tenantId}/${bundleId}` : `/tenant/app/${bundleId}`;
      return contentUid ? `${base}/${contentUid}` : base;
   };


   // --- NAVIGATION HELPERS ---
   const handleBack = () => {
      setViewingAsset(null);
      setEditingContentId(null);
      setOnboardingTenantId(null);
      setViewingTenantId(null);
      setViewingPartnerId(null);
      setDashboardEditorState(null);
      setPreviewContentId(null);
      setTenantAppRunnerProps(null);
   };

   const handleNavigate = (pageId, params = {}) => {
      handleBack();
      if (activePage === pageId) {
         window.dispatchEvent(new CustomEvent('reset-page-state', { detail: { pageId } }));
      }
      setActivePage(pageId);
      // Handle params if needed (e.g., setEditingContentId / setPreviewContentId)
      if (params.contentId) {
         if (pageId === 'app_runner') {
            setPreviewContentId(params.contentId);
         } else {
            setEditingContentId(params.contentId);
         }
      }
      if (params.tenantId) setViewingTenantId(params.tenantId);
      if (params.partnerId) setViewingPartnerId(params.partnerId);
      if (params.asset) setViewingAsset(params.asset);

      if (pageId === 'flows' && location.pathname !== '/flows') {
         navigate('/flows');
      }
   };

   // Unified helper to handle custom, published, and AIDAS app navigation and sidebar expansion
   const handleSidebarAppNavigation = useCallback(({
      itemId,
      itemType, // 'dashboard' | 'flow'
      subType, // 'project' | 'app'
      sectionKey, // e.g. 'assigned_apps', 'dashboards', 'projects', 'apps'
      sectionTitle, // 'AIDAS Apps' | 'Published Apps' | 'My Workspace'
      bundleId = null,
      expandSections = []
   }) => {
      // 1. Mark specific sidebar sub-sections as expanded in the stable ref
      expandSections.forEach(sec => {
         if (sec) sidebarExpandedRef.current[sec] = true;
      });

      // 2. Open the main parent accordion for this group
      if (sectionTitle === 'Published Apps' || sectionKey === 'pub_dash' || sectionKey === 'pub_aiml' || sectionKey === 'pub_flows') {
         setPublishedAppsExpanded(true);
      } else if (sectionTitle === 'My Workspace' || sectionKey === 'my_dash' || sectionKey === 'my_aiml' || sectionKey === 'my_flows') {
         setMyAppsExpanded(true);
      } else {
         setAidasAppsExpanded(true);
      }

      // 3. Render target page and set the corresponding properties
      if (itemType === 'dashboard' && !bundleId) {
         setActivePage('myapps_dashboard');
         setDashboardEditorState({ mode: 'view', dashboardId: itemId });
         navigate(`/dashboards/${itemId}/view`, { state: { skipReset: true } });
      } else if (itemType === 'bundle') {
         setActivePage('tenant_app_runner');
         setTenantAppRunnerProps({
            appUid: itemId,
            contentUid: null,
            bundleId: itemId,
            tenantId: currentUser?.tenantId ?? null,
            sectionKey: sectionKey,
            sectionTitle: sectionTitle
         });
         navigate(appPath(currentUser?.tenantId, itemId), { state: { skipReset: true } });
      } else {
         const targetAppUid = bundleId || itemId;
         const targetContentUid = itemId !== targetAppUid ? itemId : null;

         setActivePage('tenant_app_runner');
         setTenantAppRunnerProps({
            appUid: targetAppUid,
            contentUid: targetContentUid ?? null,   // fix: never fall back to itemId
            bundleId: targetAppUid,
            tenantId: currentUser?.tenantId ?? null,
            sectionKey: sectionKey || (subType === 'project' ? 'projects' : 'apps'),
            sectionTitle: sectionTitle
         });

         navigate(
            appPath(currentUser?.tenantId, targetAppUid, targetContentUid),
            { state: { skipReset: true } }
         );
      }
   }, [navigate, currentUser?.tenantId]);


   const { steps: tourSteps, run: tourRun, setRun: setTourRun, markSeen: markTourSeen, restartTour } = useTourController(currentUser, token, tenantFeatures, activePage);

   // Called by TourEngine when tour starts — ensures sidebar is open and all sections expanded
   // so every tour target is in the DOM and visible
   const handleTourSidebarReady = useCallback(() => {
      console.log('[PlatformLayout] 📂 Tour started — expanding sidebar and all sections');
      setSidebarOpen(true);
      setAidasAppsExpanded(true);
      setPublishedAppsExpanded(true);
      setMyAppsExpanded(true);
      setCreatorExpanded(true);
      setAppStudioExpanded(true);
      setDataExpanded(true);
      setSettingsExpanded(true);
      setAdminExpanded(true); // partners/tenants/users/roles/plans/credits/platform_settings all live here
   }, []);

   // Called by TourEngine each time the step index advances.
   // - If the step declares requiresPage, navigate there so the page DOM renders
   //   before the polling loop tries to find the target element.
   // - If the step has no requiresPage BUT a previous step navigated to a special
   //   page (e.g. settings), auto-reset to landing so the background is clean.
   const tourPageRef = React.useRef(null);
   const handleTourStepChange = useCallback((step) => {
      if (step?.requiresPage) {
         tourPageRef.current = step.requiresPage;
         setActivePage(step.requiresPage);
      } else if (tourPageRef.current && tourPageRef.current !== 'landing') {
         // Previous step was on a special page — reset to home
         tourPageRef.current = 'landing';
         setActivePage('landing');
      }
   }, []);

   // Fire unified sidebar-data endpoint — stale-while-revalidate via sessionStorage
   // One call returns both features + content; first paint uses cached data (instant).
   useEffect(() => {
      if (!currentUser?.tenantId || !token) return;
      const tid = currentUser.tenantId;
      const SIDEBAR_CACHE_KEY = `apf_sidebar_v1_${tid}`;
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

      // Step 1: Instant paint from cache
      try {
         const cached = sessionStorage.getItem(SIDEBAR_CACHE_KEY);
         if (cached) {
            const { data, ts } = JSON.parse(cached);
            if (Date.now() - ts < CACHE_TTL) {
               const { features, ...content } = data;
               setTenantFeatures(features || {});
               setMyContent(content);
            }
         }
      } catch (_) { /* ignore parse errors */ }

      // Step 2: Background fetch — updates cache + state silently
      apiFetch(`/tenants/${tid}/sidebar-data`, token)
         .then(data => {
            if (!data) return;
            const { features, ...content } = data;
            setTenantFeatures(features || {});
            setMyContent(content);
            try {
               sessionStorage.setItem(SIDEBAR_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
            } catch (_) { /* storage quota exceeded - skip */ }
         })
         .catch(e => console.error('[PlatformLayout] sidebar-data fetch failed', e))
         .finally(() => setContentLoading(false));
   }, [isTenantUser, currentUser?.tenantId, token]);

   // --- BUILD TENANT MENU (AIDAS Apps, Published Apps, My Apps) ---
   // useMemo: computed lazily from data, never triggers extra renders or setState cascade.
   const { aidasAppsMenu, publishedAppsMenu, myAppsMenu, allAppBundles, allSections } = React.useMemo(() => {
      const empty = { aidasAppsMenu: [], publishedAppsMenu: [], myAppsMenu: [], allAppBundles: [], allSections: { assigned_apps: [], dashboards: [], projects: [], apps: [] } };
      if (!myContent) return empty;
      const { assigned_apps, dashboards, projects, apps } = myContent;

      // Helper to check if item is Organization-Wide published
      const isGlobal = (item) => {
         try {
            if (!item.shared_with) return false;
            const sw = typeof item.shared_with === 'string' ? JSON.parse(item.shared_with) : item.shared_with;
            return Array.isArray(sw) && sw.includes('*');
         } catch (err) {
            console.error("Failed to parse shared_with", err);
            return false;
         }
      };

      // 1. AIDAS Apps (System-Assigned Business Applications)
      const aidasMenu = [];
      const bundledContentIds = new Set();

      if (assigned_apps && assigned_apps.length > 0) {
         assigned_apps.forEach(app => {
            const bundleName = app.name || 'App Bundle';
            const bundleContents = app.contents || [];

            const dashboardsList = bundleContents.filter(c => c.type === 'dashboard');
            const aiPredictions = bundleContents.filter(c => c.type === 'content' && c.content_type === 'project');

            bundleContents.forEach(c => bundledContentIds.add(c.id));

            const catChildren = [];

            if (dashboardsList.length > 0) {
               catChildren.push({
                  id: `aidas_${app.id}_dash`,
                  label: 'DASHBOARDS',
                  type: 'group',
                  children: dashboardsList.map(d => ({
                     id: `bundle_item_${d.id}`,
                     label: d.name,
                     icon: 'LayoutDashboard',
                     action: () => handleSidebarAppNavigation({
                        itemId: d.id,
                        itemType: 'dashboard',
                        sectionKey: 'assigned_apps',
                        sectionTitle: BRAND.name || 'AIDAS Apps',
                        bundleId: app.id,
                        expandSections: ['grp-aidas', `aidas_${app.id}`, `aidas_${app.id}_dash`]
                     })
                  }))
               });
            }

            if (aiPredictions.length > 0) {
               catChildren.push({
                  id: `aidas_${app.id}_ml`,
                  label: 'AI/ML PREDICTIONS',
                  type: 'group',
                  children: aiPredictions.map(f => ({
                     id: `bundle_item_${f.id}`,
                     label: f.name,
                     icon: 'BrainCircuit',
                     action: () => handleSidebarAppNavigation({
                        itemId: f.id,
                        itemType: 'flow',
                        sectionKey: 'assigned_apps',
                        sectionTitle: BRAND.name || 'AIDAS Apps',
                        bundleId: app.id,
                        expandSections: ['grp-aidas', `aidas_${app.id}`, `aidas_${app.id}_ml`]
                     })
                  }))
               });
            }

            const bundleNavAction = () => handleSidebarAppNavigation({
               itemId: app.id,
               itemType: 'bundle',
               sectionKey: 'assigned_apps',
               sectionTitle: BRAND.name || 'AIDAS Apps',
               bundleId: app.id,
               expandSections: ['grp-aidas', `aidas_${app.id}`]
            });

            const isDashContent = (c) =>
               c.type === 'dashboard' ||
               c.content_type === 'dashboard' ||
               isDashboardType(c.contentTypeId);
            const dashChildren = bundleContents.filter(isDashContent);
            const allFlows = bundleContents.filter(c => !isDashContent(c));

            const aiMlGenAiFlows = allFlows.filter(c => isAiMlGenAi(c.contentTypeId));
            const workflowFlows = allFlows.filter(c => !isAiMlGenAi(c.contentTypeId));

            const childrenGroups = [];
            if (dashChildren.length > 0) {
               childrenGroups.push({
                  id: `aidas_${app.id}_dash`,
                  label: 'Dashboards',
                  type: 'group',
                  children: dashChildren.map(c => ({
                     id: `bundle_${app.id}_dash_${c.id}`,
                     label: c.name,
                     icon: 'LayoutDashboard',
                     action: () => handleSidebarAppNavigation({
                        itemId: c.id,
                        itemType: 'dashboard',
                        sectionKey: 'assigned_apps',
                        sectionTitle: BRAND.name || 'AIDAS Apps',
                        bundleId: app.id,
                        expandSections: ['grp-aidas', `aidas_${app.id}`]
                     })
                  }))
               });
            }

            if (aiMlGenAiFlows.length > 0) {
               childrenGroups.push({
                  id: `aidas_${app.id}_aiml`,
                  label: 'AI/ML/GenAI',
                  type: 'group',
                  children: aiMlGenAiFlows.map(c => ({
                     id: `bundle_${app.id}_aiml_${c.id}`,
                     label: c.name,
                     icon: 'BrainCircuit',
                     action: () => handleSidebarAppNavigation({
                        itemId: c.id,
                        itemType: 'flow',
                        sectionKey: 'assigned_apps',
                        sectionTitle: BRAND.name || 'AIDAS Apps',
                        bundleId: app.id,
                        expandSections: ['grp-aidas', `aidas_${app.id}`]
                     })
                  }))
               });
            }

            if (workflowFlows.length > 0) {
               childrenGroups.push({
                  id: `aidas_${app.id}_flows`,
                  label: 'Workflows',
                  type: 'group',
                  children: workflowFlows.map(c => ({
                     id: `bundle_${app.id}_flow_${c.id}`,
                     label: c.name,
                     icon: 'Workflow',
                     action: () => handleSidebarAppNavigation({
                        itemId: c.id,
                        itemType: 'flow',
                        sectionKey: 'assigned_apps',
                        sectionTitle: BRAND.name || 'AIDAS Apps',
                        bundleId: app.id,
                        expandSections: ['grp-aidas', `aidas_${app.id}`]
                     })
                  }))
               });
            }

            aidasMenu.push({
               id: `aidas_${app.id}`,
               label: bundleName,
               icon: app.icon || 'FolderTree',
               action: bundleNavAction,
               children: childrenGroups.length > 0 ? childrenGroups : (catChildren.length > 0 ? catChildren : null)
            });
         });
      }

      // 2. Published Apps (Tenant-Wide Shared Apps)
      // 3. My Apps (User-Specific / Private Workspace Apps)
      const pubMenu = [];
      const myMenu = [];

      const buildCategoryGroup = (targetMenu, idPrefix, title, items, defaultIcon, getAction) => {
         if (items && items.length > 0) {
            targetMenu.push({
               id: `${idPrefix}`,
               label: title,
               type: 'group',
               icon: defaultIcon,
               children: items.map(item => ({
                  id: `${idPrefix}_item_${item.id}`,
                  label: item.name,
                  icon: defaultIcon,
                  action: getAction(item, idPrefix),
                  sharedBy: item.is_shared_with_me ? (item.shared_by_name || 'Someone') : null,
               }))
            });
         }
      };

      const displayDashboards = (dashboards || []).filter(d => !bundledContentIds.has(d.id));
      const displayFlows = [...(projects || []), ...(apps || [])].filter(p => !bundledContentIds.has(p.id));

      const pubDash = displayDashboards.filter(d => isGlobal(d));
      const myDash = displayDashboards.filter(d => !isGlobal(d));

      const aiMlFlows = displayFlows.filter(f => isAiMlGenAi(f.contentTypeId));
      const workFlows = displayFlows.filter(f => !isAiMlGenAi(f.contentTypeId));

      const pubAIML = aiMlFlows.filter(f => isGlobal(f));
      const myAIML = aiMlFlows.filter(f => !isGlobal(f));

      const pubWorkflows = workFlows.filter(f => isGlobal(f));
      const myWorkflows = workFlows.filter(f => !isGlobal(f));

      const dashAction = (d, prefix, groupName, sectionTitle) => () => {
         handleSidebarAppNavigation({
            itemId: d.dashboard_id || d.id,
            itemType: 'dashboard',
            sectionKey: prefix,
            sectionTitle: sectionTitle,
            expandSections: [groupName, prefix]
         });
      };

      // For "My Workspace" flows (owned by the user), open the Flow Editor directly
      // so owners can view, edit, and unlock their flows without being forced into
      // the read-only TenantAppRunnerPage view.
      // For "Published Apps" flows (shared organisation-wide), keep the app runner.
      const flowAction = (f, prefix, groupName, sectionTitle) => () => {
         const isMyWorkspace = prefix === 'my_aiml' || prefix === 'my_flows';
         if (isMyWorkspace) {
            // Navigate directly to the Flow Editor using the underlying flow_id.
            // flow_id is the actual flows table id; f.id is the Content record id.
            const targetFlowId = f.flow_id || f.id;
            setActivePage('flows');
            navigate(`/flows/${targetFlowId}`, { state: { skipReset: true } });
            setMyAppsExpanded(true);
         } else {
            handleSidebarAppNavigation({
               itemId: f.id,
               itemType: 'flow',
               subType: f.type,
               sectionKey: prefix,
               sectionTitle: sectionTitle,
               expandSections: [groupName, prefix]
            });
         }
      };

      // Build Published Apps Menu
      buildCategoryGroup(pubMenu, 'pub_dash', 'Dashboards', pubDash, 'LayoutDashboard', (d, p) => dashAction(d, p, 'grp-published', 'Published Apps'));
      buildCategoryGroup(pubMenu, 'pub_aiml', 'AI/ML/GenAI', pubAIML, 'BrainCircuit', (f, p) => flowAction(f, p, 'grp-published', 'Published Apps'));
      buildCategoryGroup(pubMenu, 'pub_flows', 'Workflows', pubWorkflows, 'Workflow', (f, p) => flowAction(f, p, 'grp-published', 'Published Apps'));

      // Build My Apps Menu
      buildCategoryGroup(myMenu, 'my_dash', 'Dashboards', myDash, 'LayoutDashboard', (d, p) => dashAction(d, p, 'grp-myapps', 'My Workspace'));
      buildCategoryGroup(myMenu, 'my_aiml', 'AI/ML/GenAI', myAIML, 'BrainCircuit', (f, p) => flowAction(f, p, 'grp-myapps', 'My Workspace'));
      buildCategoryGroup(myMenu, 'my_flows', 'Workflows', myWorkflows, 'Workflow', (f, p) => flowAction(f, p, 'grp-myapps', 'My Workspace'));

      return {
         aidasAppsMenu: aidasMenu,
         publishedAppsMenu: pubMenu,
         myAppsMenu: myMenu,
         allAppBundles: assigned_apps || [],
         allSections: { assigned_apps: assigned_apps || [], dashboards: dashboards || [], projects: projects || [], apps: apps || [] },
      };
   }, [myContent, isTenantUser, handleSidebarAppNavigation, navigate, setMyAppsExpanded, setActivePage]);



   // Pages that are controlled by React state, not by URL route.
   // When currentUser changes (e.g. after refreshUser), the location sync
   // effect must NOT overwrite these back to whatever the URL says.
   const STATE_MANAGED_PAGES = ['profile', 'credits', 'my-trash', 'support', 'my-apps'];

   useEffect(() => {
      if (currentUser) {
         const path = location.pathname;
         // --- MANDATORY DIAGNOSTIC LOGS [dbg001] ---
         // console.group('%c[Routing Debug]', 'color: #8b5cf6; font-weight: bold;');
         // console.log('Is Authenticated:', !!currentUser);
         // console.log('Token Exists:', !!token);
         // console.log('Current Path Before Reload:', path);
         // console.log('Active Page (Restored):', activePage);
         // console.log('Location State:', location.state);
         // console.groupEnd();

         // Guard: if the user is on a state-managed page (e.g. profile) and the URL
         // hasn't actually navigated to a real route (it's still /home or /), don't
         // reset activePage. This prevents refreshUser() from bouncing the user back
         // to /home after saving their profile.
         if (STATE_MANAGED_PAGES.includes(activePage) && (path === '/home' || path === '/')) {
            return;
         }

         const profileMatch = matchPath({ path: '/profile', end: true }, path);
         if (profileMatch) {
            setProfileTab(location.state?.forceTab || null);
            setActivePage('profile');
            return;
         }

         // New canonical scheme: /{tenantId}/{bundleId}/{contentUid?}
         // Also keep legacy /tenant/app/... as fallback for bookmarks / breadcrumbs.
         const tenantScopedMatch =
            matchPath({ path: '/:tenantId/:bundleId/:contentUid', end: true }, path) ||
            matchPath({ path: '/:tenantId/:bundleId', end: true }, path);

         // Only treat as an app runner URL if tenantId looks like our tenant ID prefix
         // (t_...) AND bundleId looks like a bundle (b_...)
         const looksLikeTenantApp = (m) =>
            m &&
            /^t_/.test(m.params.tenantId) &&
            /^b_/.test(m.params.bundleId);

         if (looksLikeTenantApp(tenantScopedMatch) && (isTenantUser || hasPermission(currentUser, PERMISSIONS.PAGE_APPS))) {
            setActivePage('tenant_app_runner');
            setTenantAppRunnerProps({
               appUid: tenantScopedMatch.params.bundleId,
               contentUid: tenantScopedMatch.params.contentUid ?? null,
               bundleId: tenantScopedMatch.params.bundleId,
               tenantId: tenantScopedMatch.params.tenantId,
            });
            return;
         }

         const appRunnerMatch = matchPath({ path: '/tenant/app/:appUid/:contentUid?', end: true }, path);
         if (appRunnerMatch && (isTenantUser || hasPermission(currentUser, PERMISSIONS.PAGE_APPS))) {
            setActivePage('tenant_app_runner');
            setTenantAppRunnerProps({
               appUid: appRunnerMatch.params.appUid,
               contentUid: appRunnerMatch.params.contentUid ?? null,
               bundleId: appRunnerMatch.params.appUid,
               tenantId: currentUser?.tenantId ?? null,
            });
            return;
         }

         // 2. Dashboard Standalone Handling (View/Edit/New)
         // Support both legacy BYOR routes and current dashboard routes.
         // IMPORTANT: /dashboards/create MUST be checked before /dashboards/:id
         // otherwise "create" is captured as a dashboard ID param → 404.
         const dbCreateMatch = matchPath({ path: '/dashboards/create', end: true }, path);
         if (dbCreateMatch) {
            setActivePage('dashboards');
            setDashboardEditorState({ mode: 'new' });
            return;
         }

         const dbViewMatch =
            matchPath({ path: '/analytics/byor/:id', end: true }, path) ||
            matchPath({ path: '/dashboards/:id/view', end: true }, path) ||
            matchPath({ path: '/dashboards/:id', end: true }, path);
         if (dbViewMatch) {
            const hasStudioAccess = hasAccess('dashboards');
            if (isTenantUser && !hasStudioAccess) {
               setActivePage('myapps_dashboard');
            } else {
               setActivePage('dashboards');
            }
            setDashboardEditorState({ mode: 'view', dashboardId: dbViewMatch.params.id });
            return;
         }

         const dbEditMatch =
            matchPath({ path: '/analytics/byor/:id/edit', end: true }, path) ||
            matchPath({ path: '/dashboards/:id/edit', end: true }, path);
         if (dbEditMatch) {
            setActivePage('dashboards');
            setDashboardEditorState({ mode: 'edit', dashboardId: dbEditMatch.params.id });
            return;
         }

         if (path === '/dashboards') {
            setActivePage('dashboards');
            setDashboardEditorState(null);
            return;
         }

         // 2c. Flow Editor / Runner Matching (Projects)
         // Matches /flows/123 or /flows/123?mode=...
         const flowMatch = matchPath({ path: '/flows/:flowId', end: false }, path);
         if (flowMatch) {
            // Check if it's a known "Tool" page first (Data/ML/Workflow Studio)
            // But usually those trigger from /flows?mode=... which is caught by generic logic if mode is query param?
            // Actually, Studios link to /flows?mode=... 
            // Projects link to /flows/:id
            setActivePage('flows');
            return;
         }

         // 3. Generic Page Matching
         // If we navigated to '/' with skipReset (e.g. from My Apps), don't force Landing
         if (path === '/') {
            if (location.state?.skipReset) return;
            // Protect activePage resets back to home if we are currently visiting a state-managed tab
            if (STATE_MANAGED_PAGES.includes(activePage)) return;

            // Tenant users go to their landing page; platform/partner admins go to the admin dashboard
            if (isTenantUser) {
               setActivePage('landing');
            } else {
               setActivePage('dashboard'); // hits default AdminDashboardPage
            }
            return;
         }

         // Guard: /home is a real route but state-managed pages render on top of it.
         // If activePage is state-managed, don't let /home reset it.
         if (path === '/home' && STATE_MANAGED_PAGES.includes(activePage)) {
            return;
         }

         const potentialPage = path.substring(1); // remove leading slash
         // Check for multi-segment paths like /dq/profiler
         if (path === '/dq/profiler') {
            if (hasAccess('dq-profiler')) setActivePage('dq-profiler');
            return;
         }

         const myAppsBundleMatch = matchPath({ path: '/my-apps/:bundleId', end: true }, path);
         if (myAppsBundleMatch) {
            setActivePage('my-apps');
            setTenantAppRunnerProps({ bundleId: myAppsBundleMatch.params.bundleId });
            return;
         }
         // Simple matching for pages that map 1:1 with ID
         if (potentialPage) {
            // Check if it's a known page
            if (hasAccess(potentialPage) ||
               ['tenants', 'partners', 'users', 'roles_permissions', 'plans', 'datasources', 'automations', 'alerts', 'settings', 'logs', 'my-apps', 'dashboard', 'administration', 'components', 'semantic-kb', 'datamodels', 'worklist-studio', 'worklist-editor', 'worklist-runner', 'tasks', 'audit_logs', 'admin-trash'].includes(potentialPage)) {
               setActivePage(potentialPage);
               return;
            }
         }
      }
   }, [currentUser, location, isTenantUser]);

   // Separate Security Watchdog: Gated Permission Security checks only on activePage or role changes
   useEffect(() => {
      if (currentUser && !userLoading && activePage && !hasAccess(activePage)) {
         console.warn(`[Routing Security Watchdog] Restricted access to "${activePage}". Triggering fallback...`);
         if (isTenantUser) {
            setActivePage('landing');
         } else {
            const firstPage = Object.keys(PAGE_PERMISSION_MAP).find(p => hasAccess(p));
            if (firstPage) {
               setActivePage(firstPage);
            } else {
               setActivePage('dashboard');
            }
         }
      }
   }, [activePage, currentUser, userLoading, isTenantUser]);

   if (userLoading || !currentUser) {
      return (
         <div className="flex items-center justify-center h-screen">
            <LoadingSpinner />
            <span className="ml-3 text-gray-500">Loading user session...</span>
         </div>
      );
   }

   if (userError) {
      return (
         <div className="flex items-center justify-center h-screen text-red-500">
            Error loading user data. Please try logging in again.
         </div>
      );
   }


   const renderPage = () => {
      // 1. Asset/Detail Views (Overlays/Full Page modes)
      if (viewingAsset) {
         return <AssetDetailPage token={token} assetId={viewingAsset.id} assetType={viewingAsset.type} onBack={handleBack} />;
      }



      if (onboardingTenantId) {
         return (
            <TenantOnboardingPage
               token={token}
               tenantId={onboardingTenantId}
               onBack={() => setOnboardingTenantId(null)}
            />
         );
      }

      if (viewingTenantId) {
         return <TenantDetailPage token={token} tenantId={viewingTenantId} onBack={handleBack} currentUser={currentUser} />;
      }
      if (viewingPartnerId) {
         return <PartnerDetailPage token={token} partnerId={viewingPartnerId} onBack={handleBack} currentUser={currentUser} />;
      }

      // 2. Tenant Specific Routes
      if (isTenantUser) {
         // console.log("PLATFORM_LAYOUT renderPage isTenantUser true, activePage:", activePage);
         switch (activePage) {
            case 'home': return <LandingPage setActivePage={setActivePage} />;
            case 'logs': return <ExecutionLogViewer />;
            case 'my-apps': return <MyAppsPage appBundles={allAppBundles} bundleId={tenantAppRunnerProps?.bundleId} />;
            case 'tenant_app_master':
               return <TenantAppRunnerPage
                  key={location.key}
                  navMode="master"
                  allSections={allSections}
               />;
            case 'tenant_app_section':
               return <TenantAppRunnerPage
                  key={location.key}
                  navMode="section"
                  sectionKey={tenantAppRunnerProps?.sectionKey}
                  sectionTitle={tenantAppRunnerProps?.sectionTitle}
                  sectionItems={tenantAppRunnerProps?.sectionItems}
                  allSections={allSections}
               />;
            case 'tenant_app_runner': return <TenantAppRunnerPage
               key={location.key}
               navMode="app"
               appUid={tenantAppRunnerProps?.appUid}
               contentUid={tenantAppRunnerProps?.contentUid}
               sectionKey={tenantAppRunnerProps?.sectionKey}
               sectionTitle={tenantAppRunnerProps?.sectionTitle}
               allSections={allSections}
            />;
            case 'tasks': return <TaskCenterPage token={token} currentUser={currentUser} />;
            case 'users': return <TenantUsersPage currentUser={currentUser} token={token} />;
            case 'automations': return <AutomationsPage token={token} currentUser={currentUser} />;
            case 'alerts': return <AlertRulesPage token={token} currentUser={currentUser} />;
            case 'settings': return <TenantSettingsPage token={token} />;
            case 'datasources': return <DataSourcesPage />;
            case 'datamodels': return <DataModelsPage {...{ token, currentUser, onNavigate: handleNavigate, refreshUser: refresh }} />;
            case 'datasets': return <DatasetsPage {...{ token, currentUser, onNavigate: handleNavigate, refreshUser: refresh }} />;
            case 'myapps_dashboard': // My Apps section dashboards renders read-only view
               if (dashboardEditorState?.dashboardId) {
                  return <DashboardViewPage
                     dashboardId={dashboardEditorState.dashboardId}
                     onBack={() => {
                        setDashboardEditorState(null);
                        navigate(isTenantUser ? '/my-apps' : '/dashboards');
                     }}
                     onEdit={(id) => {
                        setDashboardEditorState({ mode: 'edit', dashboardId: id });
                        navigate(`/dashboards/${id}/edit`);
                     }}
                  />;
               }
               return <LandingPage setActivePage={setActivePage} />;
            case 'dashboards':
               return <DashboardsContainer
                  dashboardEditorState={dashboardEditorState}
                  setDashboardEditorState={setDashboardEditorState}
                  handleBack={handleBack}
                  setActivePage={setActivePage}
                  token={token}
                  currentUser={currentUser}
                  onNavigate={handleNavigate}
                  refreshUser={refresh}
               />;
            case 'flows': return <FlowsPage studioMode={STUDIOS.ALL} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case 'ai-insight': return <AiInsightPage isSidebarOpen={isSidebarOpen} />;
            case 'semantic-kb': return <SemanticKnowledgeBasePage />;

            // Studio Routes
            case STUDIOS.DATA: return <FlowsPage studioMode={STUDIOS.DATA} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case STUDIOS.ML: return <FlowsPage studioMode={STUDIOS.ML} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case STUDIOS.WORKFLOW: return <FlowsPage studioMode={STUDIOS.WORKFLOW} token={token} currentUser={currentUser} onNavigate={handleNavigate} />;
            case 'credits': return <CreditsPage isAdminView={false} />;
            case 'support': return <SupportPage user={currentUser} />;
            case 'profile': return <ProfilePage forceTab={profileTab} onBack={() => setActivePage('home')} />;
            case 'dq-profiler': return <DqProfilerPage />;
            case 'my-trash': return <MyTrashPage {...{ token, currentUser, onNavigate: handleNavigate, refreshUser: refresh }} />;
            case 'about': return <AboutSettings />;
            // ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ Worklist (tenant): gated by tenantFeatures.worklist_studio + RBAC ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯
            case 'worklist-studio':
               if (!hasAccess('worklist-studio')) return <LandingPage setActivePage={setActivePage} />;
               return <WorklistStudioPage refreshContent={refresh} />;
            case 'worklist-editor':
               if (!hasAccess('worklist-editor')) return <LandingPage setActivePage={setActivePage} />;
               return <WorklistEditorPage />;
            case 'worklist-runner':
               if (!hasAccess('worklist-runner')) return <LandingPage setActivePage={setActivePage} />;
               return <WorklistRunnerPage />;
            default: return <LandingPage setActivePage={setActivePage} />;

         }
      }


      // 4. Platform Admin Routes (Existing)
      if (!hasAccess(activePage)) {
         // Show access-denied message instead of an infinite spinner
         return (
            <div className="flex flex-col h-screen items-center justify-center text-gray-500">
               <span style={{ fontSize: 48 }}>🚫</span>

               <p className="mt-4 text-lg font-semibold">Access Denied</p>
               <p className="text-sm mt-1">You do not have permission to view this page.</p>
            </div>
         );
      }

      const pageProps = { token, currentUser, onNavigate: handleNavigate, refreshUser: refresh };

      switch (activePage) {
         case 'tenants':
            return (
               <TenantsPage
                  {...pageProps}
                  onSelectTenant={setViewingTenantId}
                  onSelectOnboarding={setOnboardingTenantId}
               />
            );

         case 'partners': return <PartnersPage {...pageProps} onSelectPartner={setViewingPartnerId} />;
         case 'business_apps': return <BusinessAppsPage {...pageProps} onSelectAsset={setViewingAsset} onSelectContent={setEditingContentId} />;
         // AI/ML page hidden (Scenario A) redirect to home
         case 'contents': setActivePage('dashboard'); return null;
         case 'ai-insight': return <AiInsightPage />;
         case 'semantic-kb': return <SemanticKnowledgeBasePage />;
         case 'users': return <UsersPage {...pageProps} />;
         case 'roles_permissions': return <RolesPermissionsPage {...pageProps} />;
         case 'plans': return <PlansPage {...pageProps} />;
         case 'data-workspace': return <DataWorkspacePage {...pageProps} />;
         case 'dashboards':
            return <DashboardsContainer
               {...pageProps}
               dashboardEditorState={dashboardEditorState}
               setDashboardEditorState={setDashboardEditorState}
               handleBack={handleBack}
               setActivePage={setActivePage}
            />;
         case 'flows': return <FlowsPage {...pageProps} />;
         case 'worklist-studio': return <WorklistStudioPage refreshContent={pageProps.refreshUser} />;
         case 'worklist-editor': return <WorklistEditorPage />;
         case 'worklist-runner': return <WorklistRunnerPage />;

         case 'datasources': return <DataSourcesPage />;
         // Admins full platform settings (General/Integrations/Relay/Appearance/Security)
         // Tenant users lightweight personal settings (Appearance/Security only)
         case 'settings': return isTenantUser
            ? <SettingsPage {...pageProps} />
            : <PlatformSettings {...pageProps} />;
         case 'datamodels': return <DataModelsPage {...pageProps} />;
         case 'DatasetsPage': return <DatasetsPage {...pageProps} />;
         case 'automations': return <AutomationsPage {...pageProps} />;
         case 'alerts': return <AlertRulesPage {...pageProps} />;
         case 'credits': return <CreditsPage isAdminView={hasPermission(currentUser, PERMISSIONS.CREDITS_MANAGE)} />;
         case 'support': return <SupportPage user={currentUser} />;
         case 'profile': return <ProfilePage onBack={() => setActivePage('dashboard')} />;
         case 'dq-profiler': return <DqProfilerPage />;
         case 'admin-trash': return <AdminTrashPage {...pageProps} />;
         case 'my-trash': return <MyTrashPage {...pageProps} />;
         case 'audit_logs': return <AuditLogsPage {...pageProps} />;
         case 'demo_management': return <DemoManagementPage {...pageProps} />;
         case 'components': return <ComponentShowcasePage />;

         case 'platform_settings': return <PlatformSettings {...pageProps} />; // explicit alias
         case 'app_runner': {
            return <MLAppRunnerPage contentId={previewContentId} key={previewContentId} onBack={handleBack} />;
         }
         case 'tenant_app_runner': {
            return <TenantAppRunnerPage appUid={tenantAppRunnerProps?.appUid} contentUid={tenantAppRunnerProps?.contentUid} />;
         }
         case 'home': return <LandingPage setActivePage={setActivePage} />;
         case 'logs': return <ExecutionLogViewer />;
         case 'about': return <AboutSettings />;
         case 'dashboard':        // Platform Admin home explicit route
         case 'administration':   // Alias: /administration URL
         default: return <AdminDashboardPage {...pageProps} setActivePage={setActivePage} user={currentUser} />;
      }
   };

   const renderSidebar = () => {
      const shapeClasses = getShapeClasses(theme.shape);

      const mainNavPages = [
         // { id: 'landing', label: 'Home', icon: 'Home' },
         { id: 'home', label: 'Home', icon: 'Home' },
         { id: 'logs', label: 'Execution Status', icon: 'Loader' },
      ];

      const administrationPages = [
         { id: 'partners', label: 'Partners', icon: 'Briefcase' },
         { id: 'tenants', label: 'Tenants', icon: 'Users' },
         { id: 'users', label: 'Users', icon: 'UserCircle' },
         { id: 'roles_permissions', label: 'Roles & Permissions', icon: 'ShieldCheck' },
         { id: 'plans', label: 'Plans & Tiers', icon: 'BadgeDollarSign' },
         { id: 'credits', label: 'Credit Management', icon: 'CreditCard' },
         { id: 'support', label: 'Support Tickets', icon: 'LifeBuoy' },
         { id: 'demo_management', label: 'Public Demos', icon: 'Globe' },
         // Admin Trash removed ⎯ each user has "My Trash" in main nav for their own deleted items
         { id: 'platform_settings', label: 'Settings', icon: 'Settings' },
         { id: 'audit_logs', label: 'Audit Logs', icon: 'ShieldCheck' },
         { id: 'my-trash', label: 'Recycle Bin', icon: 'Trash2' },
      ].filter(Boolean);

      const isAdminActive = administrationPages.some(p => p.id === activePage);
      const isSettingsActive = ['users', 'settings', 'credits', 'my-trash'].includes(activePage);


      const creatorNavPages = [
         { id: 'flows', label: 'Flows', icon: 'Workflow' },
         { id: 'ai-insight', label: 'Agentic Insights', icon: 'Sparkles' },
         // { id: 'semantic-kb', label: 'Semantic Knowledge Base', icon: 'BookOpen' }, // MOVED: Now in Administration as SVB
         // { id: 'contents', label: 'AI/ML', icon: 'BrainCircuit' }, // HIDDEN: AI/ML page disabled (Scenario A)
         { id: 'dashboards', label: 'Dashboard', icon: 'LayoutDashboard' },
         { id: 'business_apps', label: 'Business Apps', icon: 'PackageCheck' },
      ].filter(Boolean);

      const appStudioNavPages = [
         { id: 'data-workspace', label: 'Unified Data Workspace', icon: 'DatabaseZap' },
         { id: 'dq-profiler', label: 'Data Profiler', icon: 'ShieldCheck', action: () => handleNavigate('dq-profiler') },
         { id: 'worklist-studio', label: 'Worklist Studio', icon: 'ClipboardList' },
      ].filter(Boolean);

      // 3.5 App Studio (Tenant sidebar items)
      // Each item is double-gated: tenant feature flag AND granular RBAC permission.
      const appStudioItems = [
         tenantFeatures.unified_data_editor && hasAccess('data-workspace') && {
            id: 'data-workspace',
            label: 'Unified Data Workspace',
            icon: 'DatabaseZap',
            action: () => handleNavigate('data-workspace')
         },
         // Data Profiler ⎯ available when the tenant has the dq_profiler feature or
         // the user has the datasources:read permission (same guard as hasAccess).
         hasAccess('dq-profiler') && {
            id: 'dq-profiler',
            label: 'Data Profiler',
            icon: 'ShieldCheck',
            action: () => handleNavigate('dq-profiler')
         },
         // Worklist Suite ⎯ shown only when the tenant feature flag is ON and the
         // logged-in user has the required permission for each sub-page.
         // Studio (design/configure): requires WORKLISTS_MANAGE
         tenantFeatures.worklist_studio && hasAccess('worklist-studio') && {
            id: 'worklist-studio',
            label: 'Worklist Studio',
            icon: 'ClipboardList',
            action: () => handleNavigate('worklist-studio')
         },
         // Editor (edit tasks): requires WORKLISTS_UPDATE
         tenantFeatures.worklist_studio && hasAccess('worklist-editor') && {
            id: 'worklist-editor',
            label: 'Worklist Editor',
            icon: 'FilePen',
            action: () => handleNavigate('worklist-editor')
         },
         // Runner (run/view tasks): requires WORKLISTS_READ
         tenantFeatures.worklist_studio && hasAccess('worklist-runner') && {
            id: 'worklist-runner',
            label: 'Worklist Runner',
            icon: 'Play',
            action: () => handleNavigate('worklist-runner')
         },
      ].filter(Boolean);


      const dataNavPages = [
         { id: 'datasources', label: 'Data Sources', icon: 'CableIcon' },
         { id: 'datamodels', label: 'Data Models', icon: 'Database' },
         hasAccess('semantic-kb') && {
            id: 'semantic-kb',
            label: 'Knowledge base',
            icon: 'Brain', // or BookOpen
            tooltip: 'Knowledge base',
            action: () => handleNavigate('semantic-kb')
         },
         // { id: 'DatasetsPage', label: 'Datasets (old)', icon: 'Layers' },
         { id: 'automations', label: 'Scheduler', icon: 'WandSparkles' },
         { id: 'alerts', label: 'Alert Rules', icon: 'Bell' },
      ].filter(Boolean);



      // Tenant Specific Nav (replacing main nav for tenants, or appending?)
      // Consolidate into one list driven by roles
      const tenantHomePages = [
         { id: 'home', label: 'Home', icon: 'Home' },
         { id: 'tasks', label: 'Task Center', icon: 'ListChecks' },
         { id: 'support', label: 'Support', icon: 'LifeBuoy' },
         { id: 'logs', label: 'Execution Logs', icon: 'ClipboardList' },
      ];


      // 2. Creator Studio Items
      const creatorItems = [
         // Unified Flows entry shown when any studio feature is enabled
         (tenantFeatures.data_studio || tenantFeatures.ml_studio || tenantFeatures.workflow_studio) && hasAccess('flows') && {
            id: 'flows',
            label: 'Flows',
            icon: 'Workflow',
            action: () => handleNavigate('flows')
         },

         // Legacy / Specific Tools
         tenantFeatures.dashboard_studio && hasAccess('dashboards') && {
            id: 'dashboards',
            label: 'Dashboards',
            icon: 'LayoutDashboard',
            action: () => handleNavigate('dashboards')
         },
         (tenantFeatures.ai_insight || tenantFeatures.AIINSIGHT_NLQ) && hasAccess('ai-insight') && {
            id: 'ai-insight',
            label: 'Agentic Insights',
            icon: 'Sparkles',
            action: () => handleNavigate('ai-insight')
         },
      ].filter(Boolean);


      // 3. Data Settings
      const dataItems = [
         // Data Sources always available for all tenants (scoped to their own connections)
         hasAccess('datasources') && {
            id: 'datasources',
            label: 'Data Sources',
            icon: 'CableIcon',
            action: () => handleNavigate('datasources')
         },
         tenantFeatures.data_studio && hasAccess('datamodels') && {
            id: 'datamodels',
            label: 'Data Models',
            icon: 'Database',
            action: () => handleNavigate('datamodels')
         },
         hasAccess('semantic-kb') && {
            id: 'semantic-kb',
            label: 'Knowledge base',
            icon: 'Brain',
            tooltip: 'Knowledge base',
            action: () => handleNavigate('semantic-kb')
         },
         // Data Explorer & Datasets - Optional, can link to Data Studio or be separate
         // Keeping them visible for now if Data Studio is enabled, or if explicitly needed.
         // {
         //    id: 'datasets',
         //    label: 'Datasets',
         //    icon: 'FileText',
         //    onClick: () => navigate('/datasets')
         // },
         hasAccess('automations') && { id: 'automations', label: 'Scheduler', icon: 'WandSparkles' },
         hasAccess('alerts') && { id: 'alerts', label: 'Alert Rules', icon: 'Bell' },
      ].filter(Boolean);

      // Accordion states are now in PlatformLayout top-level scope (accessed via closure)
      // to survive re-renders caused by state changes in the parent component.
      const canSeeCreator = creatorNavPages.some(page => hasAccess(page.id));
      const canSeeAppStudio = appStudioNavPages.some(page => hasAccess(page.id));
      const canSeeData = dataNavPages.some(page => hasAccess(page.id));


      // Helper to handle mutual exclusion for accordion behavior
      const sectionSetters = {
         aidas_apps: setAidasAppsExpanded,
         published_apps: setPublishedAppsExpanded,
         my_apps: setMyAppsExpanded,
         creator: setCreatorExpanded,
         app_studio: setAppStudioExpanded,
         data: setDataExpanded,
         admin: setAdminExpanded,
         settings: setSettingsExpanded,
      };

      const handleSectionExpand = (section, isOpen) => {
         const setter = sectionSetters[section];
         if (!setter) return;

         if (!isOpen) {
            setter(false);
            return;
         }

         setter(true);
         if (section === 'my_apps' && refreshContent) {
            refreshContent();
         }

         if (['aidas_apps', 'published_apps', 'my_apps', 'creator', 'app_studio', 'data'].includes(section)) {
            setAdminExpanded(false);
            setSettingsExpanded(false);
         } else if (section === 'admin') {
            setSettingsExpanded(false);
         } else if (section === 'settings') {
            setAdminExpanded(false);
         }
      };

      // Auto-collapse logic moved to PlatformLayout top-level scope to avoid Sidebar remount issues.

      // ... (Menu State for Collapsed Mode)
      const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
      const isSettingsMenuOpen = Boolean(settingsAnchorEl);
      const handleSettingsClick = (event) => setSettingsAnchorEl(event.currentTarget);
      const handleSettingsClose = () => setSettingsAnchorEl(null);

      const [adminAnchorEl, setAdminAnchorEl] = React.useState(null);
      const isAdminMenuOpen = Boolean(adminAnchorEl);
      const handleAdminClick = (event) => setAdminAnchorEl(event.currentTarget);
      const handleAdminClose = () => setAdminAnchorEl(null);

      // Hover handlers with small debounce on leave to prevent flicker
      const handleSidebarMouseEnter = () => {
         if (hoverLeaveTimer.current) clearTimeout(hoverLeaveTimer.current);
         setSidebarHovered(true);
      };
      const handleSidebarMouseLeave = () => {
         hoverLeaveTimer.current = setTimeout(() => setSidebarHovered(false), 180);
      };
      const toggleLock = (e) => {
         e.stopPropagation();
         setSidebarLocked(prev => {
            const next = !prev;
            localStorage.setItem('sidebar_locked', String(next));
            return next;
         });
      };

      return (
         <SidebarContext.Provider value={{
            activePage,
            tenantAppRunnerProps,
            dashboardEditorState,
            isSidebarOpen,
            sidebarExpandedRef,
            setSidebarExpandTick,
            setActivePage,
            setMobileSidebarOpen,
            handleBack,
            handleNavigate,
            isTenantUser
         }}>
            <aside
               className={`z-[60] h-full flex-shrink-0 flex flex-col group/sidebar ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
               style={{
                  width: currentW,
                  minWidth: currentW,
                  // Always fixed/overlay. The locked flag only controls whether mouse-leave collapses.
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  zIndex: 60,
                  transition: isResizing ? 'none' : 'width 0.32s cubic-bezier(0.25, 0.8, 0.25, 1), min-width 0.32s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  backgroundColor: 'var(--sidebar-bg)',
                  color: 'var(--sidebar-text, inherit)',
                  boxShadow: isExpanded ? '4px 0 24px rgba(0,0,0,0.10)' : 'none',
               }}
               onMouseEnter={handleSidebarMouseEnter}
               onMouseLeave={handleSidebarMouseLeave}
            >
               {/* ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ Right-edge: resize hit-area ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ */}
               <div
                  className="absolute top-0 h-full z-50 select-none group/divider"
                  style={{
                     left: '100%',
                     width: '8px',
                     cursor: isExpanded ? 'col-resize' : 'default'
                  }}
                  onMouseDown={isExpanded ? handleResizeStart : undefined}
               >
                  {/* 1px visible border */}
                  <div
                     className="absolute inset-y-0 left-0 w-[1px] transition-colors duration-150"
                     style={{ background: 'rgba(128,128,128,0.15)' }}
                  />
                  {/* Lock/pin button — replaces old expand/collapse toggle */}
                  {isExpanded && (
                     <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={toggleLock}
                        className="
                        absolute hidden md:flex items-center justify-center
                        w-[32px] h-[32px]
                        bg-white dark:bg-[#1e1e1e]
                        border border-gray-200 dark:border-gray-700
                        shadow-md
                        text-gray-500 dark:text-gray-400
                        hover:bg-primary hover:border-primary hover:text-white
                        transition-all duration-150
                        opacity-0 group-hover/sidebar:opacity-100
                     "
                        style={{ 
                           borderRadius: 'var(--border-radius, 8px)',
                           top: '50%', 
                           left: 0, 
                           transform: 'translate(-50%, -50%)' 
                        }}
                        title={sidebarLocked ? 'Unpin sidebar (hover mode)' : 'Pin sidebar open'}
                     >
                        <DynamicIcon
                           name={sidebarLocked ? 'PanelLeftClose' : 'PanelLeft'}
                           className="w-5 h-5"
                        />
                     </button>
                  )}
               </div>

               {/* ── Logo / Brand header ── */}
               <div
                  className="flex items-center relative flex-shrink-0 h-[60px] overflow-hidden"
                  style={{
                     paddingLeft: isExpanded ? '16px' : '0px',
                     paddingRight: isExpanded ? '16px' : '0px',
                     justifyContent: 'center',
                     transition: 'padding 0.32s cubic-bezier(0.25, 0.8, 0.25, 1)'
                  }}
               >
                  {/* bottom separator */}
                  <div
                     className="absolute bottom-0 left-3 right-3 h-[1px]"
                     style={{ background: 'linear-gradient(90deg, transparent, rgba(128,128,128,0.16), transparent)' }}
                  />

                  {isTenantUser ? (
                     /* ── Tenant: logo image or brand name ── */
                     isExpanded ? (
                        /* Expanded: full logo / tenant name */
                        getLogoLight(branding) ? (
                           <img
                              key="tenant-logo-expanded"
                              src={theme.mode === 'dark' ? (getLogoDark(branding) || getLogoLight(branding)) : getLogoLight(branding)}
                              alt="Logo"
                              className="max-w-[180px] h-[36px] object-contain transition-all duration-300"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                           />
                        ) : (
                           <span
                              className="text-xl font-black truncate leading-tight"
                              style={{ color: theme.primaryColor || 'var(--primary-color)' }}
                           >
                              {currentUser?.tenantName || 'Home'}
                           </span>
                        )
                     ) : (
                        /* Collapsed: small favicon / initial letter */
                        getCollapsedLogo(branding) ? (
                           <img
                              key="tenant-logo-collapsed"
                              src={getCollapsedLogo(branding)}
                              alt="Logo"
                              className="w-8 h-8 object-contain transition-all duration-300"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                           />
                        ) : (
                           <span
                              className="text-lg font-black"
                              style={{ color: theme.primaryColor || 'var(--primary-color)' }}
                           >
                              {(currentUser?.tenantName || 'A')[0].toUpperCase()}
                           </span>
                        )
                     )
                  ) : (
                     /* ── Admin / AIDAS brand ── */
                     isExpanded ? (
                        /* Expanded: full logo */
                        <>
                           <img
                              key="admin-logo-expanded"
                              src={BRAND.logoSrc}
                              alt={BRAND.logoAlt || BRAND.name}
                              className="max-w-[180px] h-[36px] object-contain transition-all duration-300"
                              onError={(e) => { e.currentTarget.style.display = 'none'; const fb = e.currentTarget.nextElementSibling; if (fb) fb.style.removeProperty('display'); }}
                           />
                           <span className="font-bold text-blue-500 text-base" style={{ display: 'none' }}>
                              {BRAND.name}
                           </span>
                        </>
                     ) : (
                        /* Collapsed: favicon / first letter */
                        <>
                           <img
                              key="admin-logo-collapsed"
                              src={BRAND.collapsedLogoSrc || BRAND.faviconSrc || BRAND.logoSrc}
                              alt={BRAND.name}
                              className="w-8 h-8 object-contain transition-all duration-300"
                              onError={(e) => { e.currentTarget.style.display = 'none'; const fb = e.currentTarget.nextElementSibling; if (fb) fb.style.removeProperty('display'); }}
                           />
                           <span className="font-bold text-blue-500 text-lg" style={{ display: 'none' }}>
                              {BRAND.name?.[0] ?? 'A'}
                           </span>
                        </>
                     )
                  )}
               </div>

               <nav
                  id="sidebar-nav-container"
                  className="flex-1 flex flex-col overflow-hidden relative"
               >
                  {isSidebarOpen ? (
                     /* Desktop/Expanded Sidebar: Sliding drilldown container */
                     <div
                        className="flex-1 flex w-[200%] h-full transition-transform"
                        style={{
                           transform: sidebarActiveView === 'main' ? 'translateX(0)' : 'translateX(-50%)',
                           transition: 'transform 0.32s cubic-bezier(0.25, 0.8, 0.25, 1)'
                        }}
                     >
                        {/* ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ PANEL 1: MAIN NAVIGATION LEVEL ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ */}
                        <div
                           ref={(el) => {
                              if (el) el.scrollTop = navScrollRef.current;
                           }}
                           onScroll={(e) => {
                              navScrollRef.current = e.currentTarget.scrollTop;
                           }}
                           className="w-[50%] flex-shrink-0 flex flex-col justify-between px-2 py-4 h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800"
                        >
                           <div className="space-y-1">
                              {/* 1. Main Nav */}
                              {isTenantUser ? (
                                 tenantHomePages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} isMobile={false} />)
                              ) : (
                                 mainNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} isMobile={false} />)
                              )}

                              {/* 2a. AIDAS Apps (Header triggers drilldown) */}
                              {aidasAppsMenu.length > 0 && (
                                 <button
                                    onClick={() => setSidebarActiveView('aidas_apps')}
                                    className="w-full flex items-center justify-between px-4 py-2.5 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors group cursor-pointer"
                                 >
                                    <div className="flex items-center min-w-0">
                                       <DynamicIcon name="Grid" className="w-5 h-5 mr-3 shrink-0 text-gray-500" />
                                       <span className="text-[13px] font-semibold text-[rgb(0,103,178)] dark:text-white uppercase tracking-wider truncate">{BRAND.name.split(' ')[0]} Apps</span>
                                    </div>
                                    <DynamicIcon name="ChevronRight" className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-transform" />
                                 </button>
                              )}

                              {/* 2b. Published Apps (Header triggers drilldown) */}
                              {publishedAppsMenu.length > 0 && (
                                 <button
                                    onClick={() => setSidebarActiveView('published_apps')}
                                    className="w-full flex items-center justify-between px-4 py-2.5 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors group cursor-pointer"
                                 >
                                    <div className="flex items-center min-w-0">
                                       <DynamicIcon name="Globe" className="w-5 h-5 mr-3 shrink-0 text-gray-500" />
                                       <span className="text-[13px] font-semibold text-[rgb(0,103,178)] dark:text-white uppercase tracking-wider truncate">Published Apps</span>
                                    </div>
                                    <DynamicIcon name="ChevronRight" className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-transform" />
                                 </button>
                              )}

                              {/* 2c. My Apps / My Workspace (Header triggers drilldown) */}
                              {myAppsMenu.length > 0 && (
                                 <button
                                    onClick={() => setSidebarActiveView('my_apps')}
                                    className="w-full flex items-center justify-between px-4 py-2.5 mt-4 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors group cursor-pointer"
                                 >
                                    <div className="flex items-center min-w-0">
                                       <DynamicIcon name="Folder" className="w-5 h-5 mr-3 shrink-0 text-gray-500" />
                                       <span className="text-[13px] font-semibold text-[rgb(0,103,178)] dark:text-white uppercase tracking-wider truncate">My Workspace</span>
                                    </div>
                                    <DynamicIcon name="ChevronRight" className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-transform" />
                                 </button>
                              )}

                              {/* 3. Creator Studio (Tenants) */}
                              {isTenantUser && creatorItems.length > 0 && (
                                 <>
                                    <div className="px-4 py-2 mt-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4">Creator Studio</div>
                                    <div className="space-y-1">
                                       {creatorItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                                    </div>
                                 </>
                              )}

                              {/* 3.5 App Studio (Tenants) */}
                              {isTenantUser && appStudioItems.length > 0 && (
                                 <>
                                    <div className="px-4 py-2 mt-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4">App Studio</div>
                                    <div className="space-y-1">
                                       {appStudioItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                                    </div>
                                 </>
                              )}


                              {/* 4. Data Settings (Tenants) */}
                              {isTenantUser && dataItems.length > 0 && (
                                 <>
                                    <div className="px-4 py-2 mt-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4">Data Settings</div>
                                    <div className="space-y-1">
                                       {dataItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                                    </div>
                                 </>
                              )}

                              {/* 4. Creator Studio (Admin Only) */}
                              {!isTenantUser && canSeeCreator && (
                                 <>
                                    <div className="px-4 py-2 mt-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4">Creator Studio</div>
                                    <div className="space-y-1">
                                       {creatorNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                                    </div>
                                 </>
                              )}

                              {/* 4.5 App Studio (Admin Only) */}
                              {!isTenantUser && canSeeAppStudio && (
                                 <>
                                    <div className="px-4 py-2 mt-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4">App Studio</div>
                                    <div className="space-y-1">
                                       {appStudioNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                                    </div>
                                 </>
                              )}

                              {/* 5. Data Settings (Admin Only) */}
                              {!isTenantUser && canSeeData && (
                                 <>
                                    <div className="px-4 py-2 mt-4 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-4">Data Settings</div>
                                    <div className="space-y-1">
                                       {dataNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                                    </div>
                                 </>
                              )}

                              {/* Administration (Admin Only) - Header triggers drilldown & opens first submenu page */}
                              {!isTenantUser && administrationPages.some(p => hasAccess(p.id)) && (
                                 <button
                                    onClick={() => {
                                       setSidebarActiveView('administration');
                                       const firstAdminPage = administrationPages.find(p => hasAccess(p.id));
                                       if (firstAdminPage) {
                                          setActivePage(firstAdminPage.id);
                                       }
                                    }}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 mt-4 rounded-lg transition-colors group cursor-pointer ${isAdminActive ? 'bg-primary text-[var(--primary-foreground)] shadow-md' : 'hover:bg-black/5 dark:hover:bg-white/5 text-gray-655 dark:text-gray-300'}`}
                                 >
                                    <div className="flex items-center min-w-0">
                                       <DynamicIcon name="Users" className={`w-5 h-5 mr-3 shrink-0 ${isAdminActive ? 'text-[var(--primary-foreground)]' : 'text-gray-500'}`} />
                                       <span className={`text-[13px] font-semibold uppercase tracking-wider truncate ${isAdminActive ? 'text-[var(--primary-foreground)]' : 'text-primary dark:text-white'}`}>Administration</span>
                                    </div>
                                    <DynamicIcon name="ChevronRight" className={`w-4 h-4 transition-transform ${isAdminActive ? 'text-[var(--primary-foreground)] opacity-90' : 'opacity-50 group-hover:opacity-100'}`} />
                                 </button>
                              )}

                              {/* Settings (Tenant Only) - Header triggers drilldown & opens first settings page */}
                              {isTenantUser && (hasAccess('users') || hasAccess('settings') || hasAccess('credits') || hasAccess('my-trash')) && (
                                 <button
                                    onClick={() => {
                                       setSidebarActiveView('settings');
                                       const firstSettingsPage = ['settings', 'users', 'credits', 'my-trash'].find(id => hasAccess(id));
                                       if (firstSettingsPage) {
                                          setActivePage(firstSettingsPage);
                                       }
                                    }}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 mt-4 rounded-lg transition-colors group cursor-pointer ${isSettingsActive ? 'bg-primary text-[var(--primary-foreground)] shadow-md' : 'hover:bg-black/5 dark:hover:bg-white/5 text-gray-655 dark:text-gray-300'}`}
                                 >
                                    <div className="flex items-center">
                                       <DynamicIcon name="Settings" className={`w-5 h-5 mr-3 shrink-0 ${isSettingsActive ? 'text-[var(--primary-foreground)]' : 'text-gray-500'}`} />
                                       <span className={`text-[13px] font-semibold uppercase tracking-wider ${isSettingsActive ? 'text-[var(--primary-foreground)]' : 'text-primary dark:text-white'}`}>Settings</span>
                                    </div>
                                    <DynamicIcon name="ChevronRight" className={`w-4 h-4 transition-transform ${isSettingsActive ? 'text-[var(--primary-foreground)] opacity-90' : 'opacity-50 group-hover:opacity-100'}`} />
                                 </button>
                              )}

                           </div>
                        </div>

                        {/* ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ PANEL 2: DRILLDOWN SUBMENU LEVEL ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ */}
                        <div className="w-[50%] flex-shrink-0 flex flex-col px-2 py-4 h-full">
                           {/* Faded top section back arrow header */}
                           <button
                              onClick={() => setSidebarActiveView('main')}
                              className="w-full flex items-center gap-2.5 px-4 py-3 mb-4 text-gray-550 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors border-b border-gray-100 dark:border-gray-800 text-left font-bold text-[14px] cursor-pointer"
                           >
                              <DynamicIcon name="ChevronLeft" className="w-4 h-4" />
                              <span>
                                 {sidebarActiveView === 'aidas_apps' && `${BRAND.name.split(' ')[0]} Apps`}
                                 {sidebarActiveView === 'published_apps' && 'Published Apps'}
                                 {sidebarActiveView === 'my_apps' && 'My Workspace'}
                                 {sidebarActiveView === 'administration' && 'Administration'}
                                 {sidebarActiveView === 'settings' && 'Back'}
                              </span>
                           </button>

                           <div className="space-y-1 flex-1 overflow-y-auto overflow-x-hidden pr-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                              {sidebarActiveView === 'aidas_apps' && aidasAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)}
                              {sidebarActiveView === 'published_apps' && publishedAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)}
                              {sidebarActiveView === 'my_apps' && myAppsMenu.map(bundle => <RenderMenuItem key={bundle.id} item={bundle} />)}
                              {sidebarActiveView === 'administration' && administrationPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                              {sidebarActiveView === 'settings' && (
                                 <>
                                    {hasAccess('settings') && <RenderMenuItem item={{ id: 'settings', label: 'Settings', icon: 'Settings' }} />}
                                    {hasAccess('users') && <RenderMenuItem item={{ id: 'users', label: 'Users', icon: 'Users' }} />}
                                    {hasAccess('credits') && <RenderMenuItem item={{ id: 'credits', label: 'Credit Usage', icon: 'CreditCard', action: () => setActivePage('credits') }} />}
                                    {hasAccess('my-trash') && <RenderMenuItem item={{ id: 'my-trash', label: 'Recycle Bin', icon: 'Trash2', action: () => setActivePage('my-trash') }} />}
                                 </>
                              )}
                           </div>
                        </div>
                     </div>
                  ) : (
                     /* Desktop/Collapsed Sidebar: Render icon triggers & Popups */
                     <div className="px-2 py-4 flex-1 flex flex-col justify-between overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                        <div className="space-y-1">
                           {isTenantUser ? (
                              tenantHomePages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} isMobile={false} />)
                           ) : (
                              mainNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} isMobile={false} />)
                           )}

                           {aidasAppsMenu.length > 0 && (
                              <RenderMenuItem 
                                 key="aidas_apps"
                                 item={{
                                    id: 'aidas_apps',
                                    label: `${BRAND.name.split(' ')[0]} Apps`,
                                    icon: 'Grid',
                                    children: aidasAppsMenu
                                 }} 
                              />
                           )}

                           {publishedAppsMenu.length > 0 && (
                              <RenderMenuItem 
                                 key="published_apps"
                                 item={{
                                    id: 'published_apps',
                                    label: 'Published Apps',
                                    icon: 'Globe',
                                    children: publishedAppsMenu
                                 }} 
                              />
                           )}

                           {myAppsMenu.length > 0 && (
                              <RenderMenuItem 
                                 key="my_apps"
                                 item={{
                                    id: 'my_apps',
                                    label: 'My Workspace',
                                    icon: 'Folder',
                                    children: myAppsMenu
                                 }} 
                              />
                           )}

                           {isTenantUser ? (
                              <>
                                 {creatorItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                                 {appStudioItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                                 {hasAccess('dq-profiler') && (
                                    <RenderMenuItem key="dq-profiler" item={{ id: 'dq-profiler', label: 'Data Profiler', icon: 'ShieldCheck', action: () => setActivePage('dq-profiler') }} />
                                 )}
                                 {dataItems.map(page => <RenderMenuItem key={page.id} item={page} />)}
                              </>
                           ) : (
                              <>
                                 {canSeeCreator && creatorNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                                 {canSeeAppStudio && appStudioNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                                 {canSeeData && dataNavPages.filter(p => hasAccess(p.id)).map(page => <RenderMenuItem key={page.id} item={page} />)}
                              </>
                           )}
                        </div>

                        {!isTenantUser && administrationPages.some(p => hasAccess(p.id)) && (
                           <>
                              <div
                                 className="flex justify-center py-2 my-2 cursor-pointer opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                                 onClick={handleAdminClick}
                              >
                                 <DynamicIcon name="Users" className="w-6 h-6" />
                              </div>
                              <Menu
                                 anchorEl={adminAnchorEl}
                                 open={isAdminMenuOpen}
                                 onClose={handleAdminClose}
                                 anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                                 transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              >
                                 {administrationPages.filter(page => hasAccess(page.id)).map(page => (
                                    <MenuItem key={page.id} onClick={() => {
                                       handleAdminClose();
                                       handleBack();
                                       navigate(`/${page.id}`);
                                    }}>
                                       <ListItemIcon>
                                          <DynamicIcon name={page.icon} size={20} />
                                       </ListItemIcon>
                                       <ListItemText primary={page.label} />
                                    </MenuItem>
                                 ))}
                              </Menu>
                           </>
                        )}

                        {isTenantUser && (hasAccess('users') || hasAccess('settings') || hasAccess('credits') || hasAccess('my-trash')) && (
                           <>
                              <div
                                 className="flex justify-center py-2 my-2 cursor-pointer opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                                 onClick={handleSettingsClick}
                              >
                                 <DynamicIcon name="Settings" className="w-6 h-6" />
                              </div>
                              <Menu
                                 anchorEl={settingsAnchorEl}
                                 open={isSettingsMenuOpen}
                                 onClose={handleSettingsClose}
                                 anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                                 transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              >
                                 {hasAccess('settings') && (
                                    <MenuItem onClick={() => { handleSettingsClose(); handleBack(); navigate('/settings'); }}>
                                       <ListItemIcon><DynamicIcon name="Settings" size={20} /></ListItemIcon>
                                       <ListItemText primary="Tenant Settings" />
                                    </MenuItem>
                                 )}
                                 {hasAccess('users') && (
                                    <MenuItem onClick={() => { handleSettingsClose(); handleBack(); navigate('/users'); }}>
                                       <ListItemIcon><DynamicIcon name="Users" size={20} /></ListItemIcon>
                                       <ListItemText primary="Users" />
                                    </MenuItem>
                                 )}
                                 {hasAccess('credits') && (
                                    <MenuItem onClick={() => { handleSettingsClose(); setActivePage('credits'); }}>
                                       <ListItemIcon><DynamicIcon name="CreditCard" size={20} /></ListItemIcon>
                                       <ListItemText primary="Credit Usage" />
                                    </MenuItem>
                                 )}
                                 {hasAccess('my-trash') && (
                                    <MenuItem onClick={() => { handleSettingsClose(); setActivePage('my-trash'); }}>
                                       <ListItemIcon><DynamicIcon name="Trash2" size={20} /></ListItemIcon>
                                       <ListItemText primary="Recycle Bin" />
                                    </MenuItem>
                                 )}
                              </Menu>
                           </>
                        )}
                     </div>
                  )}
               </nav>

               {/* ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ Sidebar Footer: Vercel-style user identity row ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯ */}
               <div
                  className="flex-shrink-0 px-3 py-3 flex items-center gap-2 relative"
                  style={{ backgroundColor: 'var(--sidebar-bg)' }}
               >
                  <div
                     className="absolute top-0 left-3 right-3 h-[1px]"
                     style={{ background: 'linear-gradient(90deg, transparent, rgba(128, 128, 128, 0.16), transparent)' }}
                  />
                  {isSidebarOpen ? (
                     <>
                        {/* Vercel-style unified Profile/Settings menu trigger */}
                        <div className="flex-1 min-w-0">
                           <UserProfileMenu
                              user={currentUser}
                              onLogout={logout}
                              token={token}
                              onProfileClick={() => handleNavigate('profile')}
                              onSupportClick={() => handleNavigate('support')}
                              onDocsClick={() => window.open(isTenantUser ? '/tenant-guide' : '/user-guide', '_blank')}
                              onRestartTour={restartTour}
                              isTenantUser={isTenantUser}
                              isSidebarMode={true}
                              triggerVariant="vercel-footer"
                           />
                        </div>

                        {/* Notification Bell */}
                        <div className="shrink-0">
                           <AlertNotificationCenter isSidebarOpen={isSidebarOpen} />
                        </div>
                     </>
                  ) : (
                     /* Collapsed sidebar: stacked notification bell and avatar */
                     <div className="w-full flex flex-col items-center gap-3 py-1">
                        <div className="shrink-0 flex justify-center">
                           <AlertNotificationCenter isSidebarOpen={isSidebarOpen} />
                        </div>
                        <UserProfileMenu
                           user={currentUser}
                           onLogout={logout}
                           token={token}
                           onProfileClick={() => handleNavigate('profile')}
                           onSupportClick={() => handleNavigate('support')}
                           onDocsClick={() => window.open(isTenantUser ? '/tenant-guide' : '/user-guide', '_blank')}
                           onRestartTour={restartTour}
                           isTenantUser={isTenantUser}
                           isSidebarMode={true}
                           triggerVariant="avatar"
                        />
                     </div>
                  )}
               </div>
            </aside>
         </SidebarContext.Provider>
      );
   };


   return (
      <div
         className="flex h-screen bg-gray-100 dark:bg-gray-900 font-sans"
         style={{
            fontFamily: theme.font,
            // Drive the sidebar offset as a CSS variable so the transition
            // is purely CSS-controlled and not dependent on a stale ref check.
            '--sidebar-current-w': `${currentW}px`,
            '--sidebar-offset-w': `${contentOffsetW}px`,
         }}
      >
         <TourEngine steps={tourSteps} run={tourRun} setRun={setTourRun} markSeen={markTourSeen} onSidebarReady={handleTourSidebarReady} onStepChange={handleTourStepChange} />
         {/* --- PASSWORD EXPIRY TOAST / BANNER --- */}

         <GlobalSearchPalette onNavigate={handleGlobalSearchNavigate} quickActions={quickActions} />


         <div className={`fixed inset-0 bg-black/60 z-[55] md:hidden ${mobileSidebarOpen ? 'block' : 'hidden'}`} onClick={() => setMobileSidebarOpen(false)}></div>
         {currentUser && renderSidebar()}
         <div
            className="platform-content-offset flex-1 flex flex-col overflow-hidden"
            style={{
               // Sidebar is position:fixed (out of flow). Offset the content by
               // the sidebar width using a CSS variable so the CSS transition
               // animates correctly without depending on isResizingRef (a stale ref).
               marginLeft: 'var(--sidebar-offset-w)',
               transition: isResizing ? 'none' : 'margin-left 0.32s cubic-bezier(0.25, 0.8, 0.25, 1)',
            }}
         >

            {/* Header */}
            <header className="flex-shrink-0 flex items-center justify-between px-6 h-[60px] border-b transition-colors duration-300 relative z-50"
               style={{ backgroundColor: 'var(--header-bg)', borderColor: 'rgba(0, 0, 0, 0.1)' }}>
               {/* Left side: mobile menu trigger + Breadcrumbs */}
               <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <button onClick={() => setMobileSidebarOpen(true)} className="text-gray-500 dark:text-gray-400 md:hidden shrink-0">
                     <DynamicIcon name="Menu" className="w-6 h-6" />
                  </button>
                  <div className="hidden sm:block min-w-0">
                     <GlobalBreadcrumbs
                        activePage={activePage}
                        viewingTenantId={viewingTenantId}
                        viewingPartnerId={viewingPartnerId}
                        onboardingTenantId={onboardingTenantId}
                        dashboardEditorState={dashboardEditorState}
                        editingContentId={editingContentId}
                        viewingAsset={viewingAsset}
                        previewContentId={previewContentId}
                        tenantAppRunnerProps={tenantAppRunnerProps}
                        allAppBundles={allAppBundles}
                        allSections={allSections}
                        currentUser={currentUser}
                        isTenantUser={isTenantUser}
                        handleBack={handleBack}
                        handleNavigate={handleNavigate}
                     />
                  </div>
               </div>

               {/* Right side: Global Search Bar */}
               <div className="flex items-center shrink-0 ml-4">
                  {currentUser && (
                     <div
                        onClick={() => window.dispatchEvent(new CustomEvent('open-global-search'))}
                        className="flex items-center bg-gray-100/60 dark:bg-gray-800/40 border border-gray-200/80 dark:border-gray-850 rounded-xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all duration-200 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-primary/50 w-[240px] md:w-[280px]"
                     >
                        <DynamicIcon name="Search" className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 shrink-0" />
                        <div className="bg-transparent border-none text-[13px] text-gray-400 dark:text-gray-500 w-full pr-1 flex justify-between items-center select-none pointer-events-none">
                           <span>Search anything...</span>
                           <div className="flex space-x-1">
                              <span className="bg-white dark:bg-gray-700 shadow-sm rounded px-1.5 py-0.5 text-[10px] font-mono border border-gray-200 dark:border-gray-600">Ctrl</span>
                              <span className="bg-white dark:bg-gray-700 shadow-sm rounded px-1.5 py-0.5 text-[10px] font-mono border border-gray-200 dark:border-gray-600">K</span>
                           </div>
                        </div>
                     </div>
                  )}
               </div>
            </header>

            <main className={`flex-1 ${([
               'app_runner', 'tenant_app_runner', 'platform_settings', 'settings', 'support',
               'roles_permissions', 'profile', 'ai-insight', 'users', 'plans', 'credits',
               'datasources', 'datamodels', 'datasets', 'ml_models', 'flows', 'semantic-kb',
               'my-trash', 'admin-trash', 'tenants', 'partners', 'business_apps', 'dq-profiler',
               'automations', 'alerts', 'about', 'connections', 'contents', 'dashboards',
               'myapps_dashboard', 'my-apps', 'audit_logs',
               'worklist-studio', 'worklist-editor', 'worklist-runner'
            ].includes(activePage) || onboardingTenantId) ? 'overflow-hidden' : 'overflow-auto'} p-0`}>
               {currentUser ? renderPage() : <LoadingSpinner />}
            </main>
            {/* Global Static Footer */}
            <footer className="shrink-0 py-0.5 px-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 text-[10px] text-slate-500 dark:text-slate-400 flex justify-center items-center select-none z-40">
               <span>Powered by <span className="font-bold text-slate-700 dark:text-slate-200">AIDAS</span> <span className="mx-2 text-slate-300 dark:text-slate-600">|</span> <a href="https://www.aidastech.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 transition-colors">www.aidastech.com</a></span>
            </footer>
         </div>
      </div >
   );
};

export default PlatformLayout;


